/**
 * GENERATED FILE — do not edit by hand.
 * Regenerate with: python3 scripts/build-cea-tables.py
 * (requires a one-time `pip install cea numpy` — see the script
 * header and docs/combustion.md; CEA is never a runtime dependency)
 *
 * Source: NASA CEA (Chemical Equilibrium with Applications) v3.3.2
 * (https://github.com/nasa/cea, Apache-2.0), RocketSolver HP-equilibrium
 * chamber solve (infinite-area combustor, shifting equilibrium), one call
 * per grid point.  See scripts/build-cea-tables.py for the full physics
 * and units derivation (in particular why `gammaS` is CEA's gamma_s and
 * not cp_eq/cv_eq, and why cp_eq itself is not stored).
 *
 * Reactants (CEA thermo.lib, standard storable/cryogenic injection states):
 *   lox-rp1: O2(L) at 90.17 K + RP-1 at 298.15 K
 *   lox-ch4: O2(L) at 90.17 K + CH4(L) at 111.6 K
 *
 * Grid: Pc log-spaced 0.2-30 MPa (25 pts) x O/F linear 1-5 (33 pts).
 * Every grid point converged at generation time (verified, not assumed).
 */

export type CombustionPropellants = "lox-rp1" | "lox-ch4";

export interface CeaChamberTable {
  /** Chamber stagnation pressure grid [Pa], strictly increasing. */
  readonly pcGridPa: readonly number[];
  /** Oxidizer/fuel mass-ratio grid, strictly increasing. */
  readonly ofGrid: readonly number[];
  /** Equilibrium chamber (stagnation) temperature [K], [pcIndex][ofIndex]. */
  readonly t0: readonly (readonly number[])[];
  /** Equilibrium mixture molecular weight [kg/mol]. */
  readonly mw: readonly (readonly number[])[];
  /** CEA isentropic exponent gamma_s — the frozen-flow ideal-gas surrogate;
   *  see the script header for why this is not cp_eq/cv_eq. */
  readonly gammaS: readonly (readonly number[])[];
  /** Equilibrium viscosity [Pa*s]. */
  readonly muPaS: readonly (readonly number[])[];
  /** CEA characteristic velocity c* [m/s] — validation reference only;
   *  the runtime combustor model never reads this field. */
  readonly cstar: readonly (readonly number[])[];
}

export const CEA_PROVENANCE = {
  ceaVersion: "3.3.2",
  generatedBy: "scripts/build-cea-tables.py",
} as const;

export const CEA_TABLES: Record<CombustionPropellants, CeaChamberTable> = {
  "lox-rp1": {
    pcGridPa: [
      200000.0, 246433.9, 303648.4, 374146.3, 461011.6, 568044.5, 699927.1,
      862428.9, 1062659.0, 1309376.0, 1613373.0, 1987949.0, 2449490.0,
      3018187.0, 3718918.0, 4582337.0, 5646216.0, 6957096.0, 8572321.0,
      10562550.0, 13014860.0, 16036510.0, 19759700.0, 24347300.0, 30000000.0,
    ],
    ofGrid: [
      1.0, 1.125, 1.25, 1.375, 1.5, 1.625, 1.75, 1.875, 2.0, 2.125, 2.25, 2.375,
      2.5, 2.625, 2.75, 2.875, 3.0, 3.125, 3.25, 3.375, 3.5, 3.625, 3.75, 3.875,
      4.0, 4.125, 4.25, 4.375, 4.5, 4.625, 4.75, 4.875, 5.0,
    ],
    t0: [
      [
        1420.725, 1559.198, 1875.057, 2190.84, 2454.983, 2663.635, 2823.105,
        2941.737, 3026.923, 3085.924, 3125.661, 3151.813, 3168.515, 3178.601,
        3183.979, 3185.943, 3185.385, 3182.927, 3179.018, 3173.983, 3168.063,
        3161.439, 3154.25, 3146.603, 3138.581, 3130.251, 3121.664, 3112.862,
        3103.879, 3094.742, 3085.473, 3076.091, 3066.609,
      ],
      [
        1423.643, 1560.482, 1875.253, 2192.227, 2459.434, 2672.417, 2836.368,
        2959.041, 3047.579, 3109.142, 3150.706, 3178.1, 3195.617, 3206.216,
        3211.897, 3214.015, 3213.503, 3211.015, 3207.017, 3201.849, 3195.763,
        3188.948, 3181.548, 3173.673, 3165.411, 3156.83, 3147.983, 3138.914,
        3129.658, 3120.243, 3110.692, 3101.023, 3091.252,
      ],
      [
        1427.065, 1562.031, 1875.432, 2193.496, 2463.593, 2680.81, 2849.259,
        2976.067, 3068.084, 3132.336, 3175.833, 3204.549, 3222.935, 3234.084,
        3240.091, 3242.378, 3241.921, 3239.406, 3235.32, 3230.018, 3223.763,
        3216.752, 3209.134, 3201.025, 3192.515, 3183.674, 3174.559, 3165.214,
        3155.676, 3145.973, 3136.13, 3126.164, 3116.093,
      ],
      [
        1431.047, 1563.893, 1875.596, 2194.655, 2467.472, 2688.808, 2861.759,
        2992.788, 3088.41, 3155.483, 3201.025, 3231.145, 3250.46, 3262.199,
        3268.558, 3271.03, 3270.637, 3268.099, 3263.926, 3258.488, 3252.061,
        3244.848, 3237.006, 3228.655, 3219.889, 3210.781, 3201.388, 3191.758,
        3181.928, 3171.927, 3161.781, 3151.509, 3141.127,
      ],
      [
        1435.647, 1566.123, 1875.746, 2195.712, 2471.08, 2696.412, 2873.854,
        3009.179, 3108.529, 3178.556, 3226.261, 3257.875, 3278.181, 3290.553,
        3297.293, 3299.966, 3299.648, 3297.092, 3292.833, 3287.258, 3280.654,
        3273.235, 3265.163, 3256.563, 3247.532, 3238.147, 3228.468, 3218.543,
        3208.41, 3198.102, 3187.642, 3177.052, 3166.348,
      ],
      [
        1440.918, 1568.778, 1875.886, 2196.675, 2474.429, 2703.621, 2885.526,
        3025.213, 3128.41, 3201.53, 3251.519, 3284.722, 3306.087, 3319.138,
        3326.288, 3329.182, 3328.951, 3326.383, 3322.039, 3316.326, 3309.542,
        3301.91, 3293.601, 3284.744, 3275.441, 3265.77, 3255.794, 3245.564,
        3235.119, 3224.491, 3213.706, 3202.787, 3191.75,
      ],
      [
        1446.906, 1571.926, 1876.02, 2197.552, 2477.531, 2710.438, 2896.764,
        3040.864, 3148.024, 3224.374, 3276.776, 3311.669, 3334.166, 3347.945,
        3355.538, 3358.674, 3358.541, 3355.967, 3351.541, 3345.688, 3338.72,
        3330.871, 3322.318, 3313.197, 3303.611, 3293.645, 3283.363, 3272.817,
        3262.048, 3251.089, 3239.969, 3228.708, 3217.326,
      ],
      [
        1453.654, 1575.632, 1876.15, 2198.35, 2480.398, 2716.868, 2907.555,
        3056.108, 3167.34, 3247.059, 3302.008, 3338.697, 3362.403, 3376.963,
        3385.035, 3388.436, 3388.414, 3385.842, 3381.336, 3375.341, 3368.186,
        3360.115, 3351.311, 3341.916, 3332.04, 3321.769, 3311.17, 3300.296,
        3289.192, 3277.892, 3266.423, 3254.809, 3243.068,
      ],
      [
        1461.195, 1579.966, 1876.283, 2199.075, 2483.043, 2722.917, 2917.89,
        3070.922, 3186.326, 3269.554, 3327.186, 3365.785, 3390.784, 3406.182,
        3414.771, 3418.461, 3418.566, 3416.002, 3411.419, 3405.283, 3397.937,
        3389.637, 3380.575, 3370.899, 3360.723, 3350.136, 3339.209, 3327.997,
        3316.546, 3304.891, 3293.061, 3281.081, 3268.969,
      ],
      [
        1469.555, 1584.997, 1876.427, 2199.734, 2485.479, 2728.593, 2927.763,
        3085.284, 3204.95, 3291.825, 3352.284, 3392.912, 3419.291, 3435.59,
        3444.737, 3448.742, 3448.99, 3446.444, 3441.786, 3435.508, 3427.968,
        3419.434, 3410.106, 3400.14, 3389.654, 3378.741, 3367.474, 3355.912,
        3344.101, 3332.079, 3319.876, 3307.516, 3295.02,
      ],
      [
        1478.753, 1590.791, 1876.592, 2200.333, 2487.718, 2733.906, 2937.17,
        3099.173, 3223.183, 3313.838, 3377.269, 3420.052, 3447.906, 3465.173,
        3474.922, 3479.272, 3479.68, 3477.161, 3472.434, 3466.012, 3458.275,
        3449.5, 3439.899, 3429.634, 3418.827, 3407.577, 3395.959, 3384.035,
        3371.851, 3359.449, 3346.858, 3334.105, 3321.21,
      ],
      [
        1488.799, 1597.408, 1876.794, 2200.878, 2489.774, 2738.866, 2946.107,
        3112.57, 3240.991, 3335.558, 3402.111, 3447.18, 3476.611, 3494.916,
        3505.316, 3510.041, 3510.629, 3508.148, 3503.355, 3496.791, 3488.852,
        3479.831, 3469.949, 3459.375, 3448.238, 3436.639, 3424.657, 3412.357,
        3399.788, 3386.991, 3373.999, 3360.838, 3347.53,
      ],
      [
        1499.698, 1604.902, 1877.058, 2201.375, 2491.658, 2743.487, 2954.577,
        3125.46, 3258.347, 3356.949, 3426.774, 3474.267, 3505.383, 3524.803,
        3535.907, 3541.041, 3541.829, 3539.398, 3534.544, 3527.837, 3519.694,
        3510.421, 3500.25, 3489.357, 3477.878, 3465.918, 3453.56, 3440.871,
        3427.902, 3414.697, 3401.288, 3387.704, 3373.968,
      ],
      [
        1511.449, 1613.318, 1877.417, 2201.829, 2493.382, 2747.782, 2962.582,
        3137.83, 3275.22, 3377.974, 3451.224, 3501.285, 3534.199, 3554.818,
        3566.68, 3572.261, 3573.271, 3570.903, 3565.994, 3559.145, 3550.794,
        3541.263, 3530.794, 3519.572, 3507.74, 3495.407, 3482.659, 3469.567,
        3456.184, 3442.555, 3428.715, 3414.692, 3400.512,
      ],
      [
        1524.045, 1622.691, 1877.922, 2202.247, 2494.958, 2751.765, 2970.127,
        3149.668, 3291.585, 3398.597, 3475.422, 3528.2, 3563.034, 3584.939,
        3597.622, 3603.689, 3604.947, 3602.656, 3597.698, 3590.708, 3582.145,
        3572.349, 3561.574, 3550.014, 3537.816, 3525.096, 3511.945, 3498.435,
        3484.623, 3470.554, 3456.266, 3441.79, 3427.149,
      ],
      [
        1537.477, 1633.044, 1878.648, 2202.635, 2496.397, 2755.451, 2977.22,
        3160.965, 3307.415, 3418.781, 3499.33, 3554.98, 3591.862, 3615.149,
        3628.717, 3635.313, 3636.846, 3634.646, 3629.648, 3622.518, 3613.74,
        3603.673, 3592.583, 3580.672, 3568.096, 3554.976, 3541.408, 3527.464,
        3513.207, 3498.683, 3483.931, 3468.983, 3453.864,
      ],
      [
        1551.732, 1644.392, 1879.697, 2203.002, 2497.71, 2758.855, 2983.871,
        3171.718, 3322.688, 3438.49, 3522.908, 3581.588, 3620.653, 3645.423,
        3659.947, 3667.119, 3668.957, 3666.866, 3661.834, 3654.565, 3645.571,
        3635.225, 3623.81, 3611.538, 3598.571, 3585.037, 3571.035, 3556.643,
        3541.924, 3526.927, 3511.694, 3496.257, 3480.644,
      ],
      [
        1566.795, 1656.74, 1881.215, 2203.357, 2498.908, 2761.992, 2990.092,
        3181.925, 3337.383, 3457.688, 3546.115, 3607.985, 3649.377, 3675.738,
        3691.294, 3699.093, 3701.268, 3699.304, 3694.248, 3686.842, 3677.627,
        3666.996, 3655.247, 3642.601, 3629.23, 3615.267, 3600.816, 3585.959,
        3570.761, 3555.274, 3539.542, 3523.598, 3507.471,
      ],
      [
        1582.648, 1670.083, 1883.391, 2203.713, 2500.001, 2764.879, 2995.897,
        3191.585, 3351.484, 3476.342, 3568.909, 3634.133, 3678.0, 3706.068,
        3722.739, 3731.22, 3733.765, 3731.949, 3726.878, 3719.338, 3709.899,
        3698.977, 3686.882, 3673.851, 3660.062, 3645.654, 3630.738, 3615.398,
        3599.703, 3583.708, 3567.458, 3550.988, 3534.329,
      ],
      [
        1599.272, 1684.412, 1886.462, 2204.089, 2500.999, 2767.531, 3001.299,
        3200.704, 3364.977, 3494.419, 3591.248, 3659.99, 3706.487, 3736.385,
        3754.259, 3763.481, 3766.435, 3764.788, 3759.713, 3752.043, 3742.376,
        3731.155, 3718.705, 3705.275, 3691.053, 3676.185, 3660.786, 3644.945,
        3628.735, 3612.213, 3595.426, 3578.411, 3561.2,
      ],
      [
        1616.646, 1699.984, 1890.697, 2204.511, 2501.913, 2769.964, 3006.316,
        3209.289, 3377.85, 3511.89, 3613.091, 3685.512, 3734.8, 3766.66,
        3785.832, 3795.858, 3799.262, 3797.809, 3792.742, 3784.943, 3775.047,
        3763.519, 3750.703, 3736.861, 3722.19, 3706.845, 3690.945, 3674.585,
        3657.84, 3640.771, 3623.427, 3605.847, 3588.064,
      ],
      [
        1634.746, 1716.609, 1896.375, 2205.016, 2502.755, 2772.192, 3010.964,
        3217.348, 3390.098, 3528.729, 3634.395, 3710.654, 3762.901, 3796.861,
        3817.432, 3828.333, 3832.229, 3830.997, 3825.951, 3818.028, 3807.899,
        3796.056, 3782.863, 3768.594, 3753.459, 3737.619, 3721.199, 3704.3,
        3687.001, 3669.364, 3651.442, 3633.276, 3614.901,
      ],
      [
        1653.549, 1734.247, 1903.744, 2205.658, 2503.536, 2774.231, 3015.261,
        3224.894, 3401.717, 3544.911, 3655.122, 3735.372, 3790.747, 3826.953,
        3849.032, 3860.882, 3865.321, 3864.336, 3859.327, 3851.283, 3840.919,
        3828.753, 3815.171, 3800.461, 3784.844, 3768.49, 3751.532, 3734.073,
        3716.198, 3697.973, 3679.452, 3660.679, 3641.689,
      ],
      [
        1673.028, 1752.867, 1912.988, 2206.516, 2504.272, 2776.097, 3019.226,
        3231.941, 3412.708, 3560.416, 3675.231, 3759.617, 3818.294, 3856.9,
        3880.605, 3893.484, 3898.516, 3897.811, 3892.853, 3884.695, 3874.092,
        3861.595, 3847.61, 3832.444, 3816.328, 3799.441, 3781.924, 3763.885,
        3745.413, 3726.577, 3707.434, 3688.031, 3668.406,
      ],
      [
        1693.156, 1772.441, 1924.205, 2207.7, 2504.981, 2777.803, 3022.878,
        3238.506, 3423.074, 3575.23, 3694.688, 3783.345, 3845.496, 3886.663,
        3912.118, 3926.114, 3931.796, 3931.404, 3926.514, 3918.246, 3907.403,
        3894.565, 3880.166, 3864.527, 3847.893, 3830.453, 3812.355, 3793.714,
        3774.622, 3755.153, 3735.366, 3715.311, 3695.028,
      ],
    ],
    mw: [
      [
        0.01420979, 0.01506691, 0.01591974, 0.01678582, 0.01760809, 0.01836631,
        0.01905851, 0.0196897, 0.0202651, 0.02079052, 0.0212728, 0.02171868,
        0.02213389, 0.02252292, 0.02288926, 0.0232356, 0.02356407, 0.0238764,
        0.02417399, 0.02445804, 0.02472957, 0.02498945, 0.02523846, 0.02547729,
        0.02570654, 0.02592678, 0.02613851, 0.02634217, 0.02653819, 0.02672696,
        0.02690884, 0.02708414, 0.02725318,
      ],
      [
        0.01422167, 0.01507343, 0.01592, 0.01678775, 0.01761475, 0.01838027,
        0.0190806, 0.01971967, 0.02030212, 0.02083346, 0.02132049, 0.02177009,
        0.02218817, 0.02257944, 0.02294752, 0.02329523, 0.02362477, 0.02393793,
        0.02423616, 0.0245207, 0.02479257, 0.02505269, 0.02530185, 0.02554074,
        0.02576998, 0.02599014, 0.02620172, 0.02640519, 0.02660097, 0.02678946,
        0.02697101, 0.02714595, 0.02731459,
      ],
      [
        0.01423578, 0.01508134, 0.01592024, 0.01678951, 0.01762099, 0.01839363,
        0.01910212, 0.01974923, 0.02033896, 0.02087648, 0.02136849, 0.02182198,
        0.02224308, 0.02263669, 0.02300659, 0.02335571, 0.02368636, 0.02400037,
        0.02429926, 0.02458429, 0.02485652, 0.02511688, 0.02536617, 0.0256051,
        0.02583431, 0.02605438, 0.0262658, 0.02646906, 0.02666458, 0.02685276,
        0.02703396, 0.02720851, 0.02737673,
      ],
      [
        0.01425243, 0.0150909, 0.01592046, 0.01679113, 0.01762681, 0.01840638,
        0.01912303, 0.01977832, 0.02037558, 0.02091954, 0.02141676, 0.02187434,
        0.0222986, 0.02269466, 0.02306646, 0.02341705, 0.02374884, 0.02406374,
        0.0243633, 0.02464882, 0.02492141, 0.025182, 0.02543142, 0.02567039,
        0.02589956, 0.02611951, 0.02633076, 0.02653379, 0.02672902, 0.02691687,
        0.02709769, 0.02727182, 0.02743959,
      ],
      [
        0.01427199, 0.01510243, 0.01592068, 0.0167926, 0.01763222, 0.01841851,
        0.01914329, 0.0198069, 0.02041192, 0.02096259, 0.02146527, 0.02192714,
        0.02235473, 0.02275335, 0.02312713, 0.02347926, 0.02381223, 0.02412804,
        0.02442829, 0.02471432, 0.02498726, 0.02524808, 0.02549762, 0.02573662,
        0.02596573, 0.02618555, 0.0263966, 0.02659937, 0.0267943, 0.02698178,
        0.0271622, 0.02733589, 0.02750318,
      ],
      [
        0.01429482, 0.01511627, 0.01592091, 0.01679394, 0.01763725, 0.01843003,
        0.01916288, 0.01983492, 0.02044792, 0.02100557, 0.02151398, 0.02198037,
        0.02241144, 0.02281275, 0.0231886, 0.02354232, 0.02387653, 0.02419327,
        0.02449423, 0.02478077, 0.02505407, 0.02531512, 0.02556477, 0.02580378,
        0.02603282, 0.02625249, 0.02646332, 0.02666581, 0.0268604, 0.02704751,
        0.02722749, 0.02740071, 0.02756748,
      ],
      [
        0.01432129, 0.01513282, 0.01592114, 0.01679517, 0.01764192, 0.01844093,
        0.01918177, 0.01986234, 0.02048353, 0.02104844, 0.02156285, 0.02203397,
        0.02246872, 0.02287285, 0.02325086, 0.02360625, 0.02394173, 0.02425945,
        0.02456113, 0.0248482, 0.02512186, 0.02538313, 0.02563288, 0.02587189,
        0.02610084, 0.02632034, 0.02653093, 0.02673312, 0.02692735, 0.02711404,
        0.02729356, 0.02746627, 0.02763249,
      ],
      [
        0.01435179, 0.0151525, 0.0159214, 0.01679628, 0.01764623, 0.01845123,
        0.01919995, 0.01988909, 0.02051868, 0.02109113, 0.02161183, 0.02208794,
        0.02252654, 0.02293363, 0.02331392, 0.02367105, 0.02400786, 0.02432658,
        0.024629, 0.02491661, 0.02519064, 0.02545212, 0.02570197, 0.02594096,
        0.0261698, 0.02638911, 0.02659944, 0.02680129, 0.02699513, 0.02718137,
        0.0273604, 0.02753257, 0.02769821,
      ],
      [
        0.01438671, 0.01517579, 0.01592172, 0.0167973, 0.01765022, 0.01846093,
        0.01921738, 0.01991515, 0.02055333, 0.02113359, 0.02166086, 0.02214221,
        0.02258489, 0.02299509, 0.02337776, 0.02373671, 0.0240749, 0.02439466,
        0.02469786, 0.02498601, 0.0252604, 0.0255221, 0.02577202, 0.02601099,
        0.0262397, 0.02645879, 0.02666883, 0.02687033, 0.02706375, 0.02724951,
        0.02742801, 0.0275996, 0.02776462,
      ],
      [
        0.01442644, 0.01520318, 0.01592213, 0.01679823, 0.01765389, 0.01847004,
        0.01923407, 0.01994047, 0.0205874, 0.02117574, 0.02170989, 0.02219676,
        0.02264372, 0.0230572, 0.02344237, 0.02380323, 0.02414286, 0.0244637,
        0.02476769, 0.02505641, 0.02533116, 0.02559306, 0.02584306, 0.02608198,
        0.02631055, 0.0265294, 0.02673912, 0.02694023, 0.02713319, 0.02731844,
        0.02749637, 0.02766735, 0.02783171,
      ],
      [
        0.01447137, 0.01523519, 0.01592267, 0.01679909, 0.01765727, 0.01847858,
        0.01924999, 0.019965, 0.02062083, 0.02121753, 0.02175887, 0.02225154,
        0.02270301, 0.02311995, 0.02350775, 0.0238706, 0.02421174, 0.0245337,
        0.02483851, 0.0251278, 0.02540293, 0.02566503, 0.02591509, 0.02615394,
        0.02638234, 0.02660093, 0.0268103, 0.02701099, 0.02720346, 0.02738817,
        0.02756549, 0.02773582, 0.02789947,
      ],
      [
        0.01452189, 0.01527237, 0.01592344, 0.01679988, 0.01766038, 0.01848657,
        0.01926514, 0.01998872, 0.02065358, 0.02125888, 0.02180772, 0.0223065,
        0.02276273, 0.02318332, 0.02357389, 0.02393883, 0.02428154, 0.02460467,
        0.02491032, 0.0252002, 0.0254757, 0.025738, 0.0259881, 0.02622688,
        0.02645508, 0.02667338, 0.02688238, 0.02708261, 0.02727456, 0.02745867,
        0.02763536, 0.02780498, 0.0279679,
      ],
      [
        0.01457837, 0.01531524, 0.01592454, 0.01680061, 0.01766323, 0.01849402,
        0.01927952, 0.02001159, 0.02068556, 0.02129972, 0.02185639, 0.02236158,
        0.02282284, 0.02324727, 0.02364076, 0.0240079, 0.02435225, 0.0246766,
        0.02498313, 0.0252736, 0.02554948, 0.02581197, 0.02606211, 0.02630079,
        0.02652877, 0.02674675, 0.02695534, 0.02715508, 0.02734646, 0.02752995,
        0.02770595, 0.02787483, 0.02803696,
      ],
      [
        0.01464117, 0.01536436, 0.01592615, 0.01680131, 0.01766585, 0.01850095,
        0.01929314, 0.02003357, 0.02071674, 0.02133998, 0.02190479, 0.02241672,
        0.02288329, 0.02331179, 0.02370835, 0.0240778, 0.02442388, 0.02474949,
        0.02505693, 0.02534802, 0.02562428, 0.02588696, 0.02613712, 0.02637567,
        0.02660341, 0.02682104, 0.02702918, 0.02722839, 0.02741918, 0.02760199,
        0.02777726, 0.02794535, 0.02810664,
      ],
      [
        0.01471066, 0.01542025, 0.01592853, 0.01680198, 0.01766826, 0.01850739,
        0.019306, 0.02005466, 0.02074705, 0.02137959, 0.02195286, 0.02247187,
        0.02294403, 0.02337683, 0.02377664, 0.02414852, 0.02449641, 0.02482335,
        0.02513173, 0.02542346, 0.0257001, 0.02596296, 0.02621312, 0.02645153,
        0.026679, 0.02689624, 0.0271039, 0.02730254, 0.02749268, 0.02767478,
        0.02784926, 0.02801652, 0.02817691,
      ],
      [
        0.01478718, 0.01548343, 0.01593204, 0.01680265, 0.01767047, 0.01851336,
        0.01931811, 0.02007483, 0.02077645, 0.02141846, 0.02200051, 0.02252695,
        0.02300502, 0.02344236, 0.0238456, 0.02422006, 0.02456985, 0.02489817,
        0.02520753, 0.02549991, 0.02577694, 0.02603997, 0.02629012, 0.02652836,
        0.02675553, 0.02697236, 0.02717949, 0.02737752, 0.02756697, 0.0277483,
        0.02792195, 0.02808832, 0.02824777,
      ],
      [
        0.01487106, 0.0155544, 0.01593722, 0.01680336, 0.0176725, 0.01851889,
        0.01932948, 0.02009407, 0.02080489, 0.02145653, 0.02204766, 0.02258188,
        0.0230662, 0.02350834, 0.02391522, 0.02429238, 0.02464418, 0.02497395,
        0.02528432, 0.02557738, 0.02585481, 0.026118, 0.02636812, 0.02660617,
        0.026833, 0.02704937, 0.02725594, 0.02745332, 0.02764202, 0.02782253,
        0.0279953, 0.02816072, 0.02831917,
      ],
      [
        0.01496262, 0.01563363, 0.01594479, 0.01680414, 0.01767437, 0.018524,
        0.01934015, 0.02011237, 0.02083232, 0.02149373, 0.02209422, 0.0226366,
        0.0231275, 0.02357473, 0.02398545, 0.02436548, 0.02471938, 0.02505068,
        0.02536211, 0.02565586, 0.02593369, 0.02619703, 0.02644711, 0.02668493,
        0.0269114, 0.02712728, 0.02733324, 0.02752991, 0.02771782, 0.02789746,
        0.02806929, 0.02823371, 0.02839109,
      ],
      [
        0.01506214, 0.01572157, 0.01595573, 0.01680507, 0.01767611, 0.01852872,
        0.01935013, 0.02012974, 0.02085871, 0.02152997, 0.02214011, 0.02269101,
        0.02318885, 0.02364148, 0.02405628, 0.02443934, 0.02479546, 0.02512835,
        0.02544089, 0.02573536, 0.02601359, 0.02627708, 0.02652709, 0.02676467,
        0.02699073, 0.02720606, 0.02741138, 0.02760729, 0.02779435, 0.02797306,
        0.02814389, 0.02830724, 0.0284635,
      ],
      [
        0.01516992, 0.01581864, 0.01597126, 0.01680623, 0.01767773, 0.01853308,
        0.01935944, 0.02014618, 0.02088403, 0.02156521, 0.02218525, 0.02274502,
        0.02325019, 0.02370853, 0.02412765, 0.02451392, 0.02487239, 0.02520695,
        0.02552065, 0.02581586, 0.0260945, 0.02635813, 0.02660805, 0.02684535,
        0.02707097, 0.02728572, 0.02749033, 0.02768543, 0.02787158, 0.02804931,
        0.02821907, 0.02838129, 0.02853637,
      ],
      [
        0.01528618, 0.01589885, 0.0159928, 0.01680777, 0.01767928, 0.01853711,
        0.01936812, 0.0201617, 0.02090826, 0.02159937, 0.02222953, 0.02279856,
        0.02331144, 0.02377583, 0.02419953, 0.02458921, 0.02495016, 0.02528649,
        0.02560139, 0.02589737, 0.02617643, 0.02644019, 0.02668999, 0.02692697,
        0.02715211, 0.02736623, 0.02757008, 0.02776431, 0.0279495, 0.02812617,
        0.0282948, 0.02845583, 0.02860964,
      ],
      [
        0.01541114, 0.01598386, 0.01602183, 0.0168099, 0.01768079, 0.01854084,
        0.0193762, 0.02017632, 0.02093139, 0.0216324, 0.02227288, 0.02285151,
        0.0233725, 0.02384331, 0.02427188, 0.02466517, 0.02502874, 0.02536693,
        0.0256831, 0.02597988, 0.02625936, 0.02652323, 0.0267729, 0.02700953,
        0.02723414, 0.02744758, 0.02765061, 0.02784391, 0.02802807, 0.02820362,
        0.02837105, 0.02853081, 0.02868329,
      ],
      [
        0.01554499, 0.01607544, 0.01605977, 0.01681293, 0.0176823, 0.01854429,
        0.0193837, 0.02019006, 0.0209534, 0.02166425, 0.02231521, 0.02290379,
        0.0234333, 0.0239109, 0.02434465, 0.02474177, 0.02510812, 0.02544828,
        0.02576577, 0.02606338, 0.02634329, 0.02660726, 0.02685676, 0.02709301,
        0.02731704, 0.02752974, 0.0277319, 0.0279242, 0.02810726, 0.02828162,
        0.02844778, 0.02860619, 0.02875727,
      ],
      [
        0.01568786, 0.01617374, 0.01610773, 0.0168173, 0.0176839, 0.01854752,
        0.01939068, 0.02020295, 0.0209743, 0.02169488, 0.02235644, 0.02295529,
        0.02349374, 0.02397853, 0.02441777, 0.02481897, 0.02518827, 0.0255305,
        0.02584939, 0.02614786, 0.0264282, 0.02669226, 0.02694157, 0.02717739,
        0.0274008, 0.0276127, 0.02781392, 0.02800516, 0.02818704, 0.02836013,
        0.02852494, 0.02868194, 0.02883153,
      ],
      [
        0.01583983, 0.01627891, 0.01616648, 0.01682367, 0.01768566, 0.01855055,
        0.01939716, 0.02021502, 0.0209941, 0.02172426, 0.02239649, 0.0230059,
        0.02355372, 0.02404612, 0.0244912, 0.02489673, 0.02526916, 0.02561359,
        0.02593395, 0.02623331, 0.02651409, 0.02677823, 0.02702731, 0.02726266,
        0.02748538, 0.02769643, 0.02789664, 0.02808674, 0.02826737, 0.02843912,
        0.0286025, 0.02875799, 0.02890602,
      ],
    ],
    gammaS: [
      [
        1.295345, 1.315452, 1.296123, 1.26224, 1.22485, 1.192907, 1.169098,
        1.151732, 1.139364, 1.130979, 1.125487, 1.121892, 1.119477, 1.117793,
        1.116572, 1.115656, 1.114947, 1.114388, 1.11394, 1.113577, 1.113283,
        1.113046, 1.112858, 1.112712, 1.112603, 1.112528, 1.112485, 1.11247,
        1.112484, 1.112523, 1.112588, 1.112678, 1.112791,
      ],
      [
        1.292546, 1.314341, 1.296525, 1.263809, 1.227512, 1.195781, 1.171707,
        1.153968, 1.141235, 1.132546, 1.126837, 1.123098, 1.12059, 1.118847,
        1.117586, 1.116642, 1.115915, 1.115342, 1.114883, 1.114513, 1.114213,
        1.113972, 1.113781, 1.113634, 1.113524, 1.11345, 1.113408, 1.113396,
        1.113412, 1.113455, 1.113524, 1.113618, 1.113737,
      ],
      [
        1.289461, 1.31302, 1.296888, 1.265263, 1.230072, 1.198632, 1.174341,
        1.156247, 1.143148, 1.134148, 1.128211, 1.12432, 1.121716, 1.119909,
        1.118607, 1.117635, 1.116887, 1.116299, 1.11583, 1.115452, 1.115147,
        1.114902, 1.114709, 1.11456, 1.11445, 1.114376, 1.114336, 1.114326,
        1.114345, 1.114392, 1.114465, 1.114565, 1.11469,
      ],
      [
        1.286117, 1.311467, 1.297216, 1.266608, 1.232524, 1.201449, 1.176994,
        1.158565, 1.145104, 1.135784, 1.129609, 1.12556, 1.122853, 1.12098,
        1.119634, 1.118632, 1.117863, 1.117261, 1.11678, 1.116395, 1.116084,
        1.115835, 1.115639, 1.115489, 1.115379, 1.115306, 1.115267, 1.11526,
        1.115282, 1.115333, 1.115412, 1.115517, 1.115648,
      ],
      [
        1.28255, 1.309662, 1.29751, 1.267849, 1.234865, 1.204223, 1.179658,
        1.16092, 1.147101, 1.137456, 1.131034, 1.126817, 1.124001, 1.122059,
        1.120666, 1.119633, 1.118843, 1.118225, 1.117734, 1.11734, 1.117023,
        1.116771, 1.116572, 1.116421, 1.116311, 1.11624, 1.116202, 1.116198,
        1.116224, 1.116279, 1.116363, 1.116474, 1.116612,
      ],
      [
        1.278809, 1.307588, 1.297773, 1.268992, 1.237092, 1.206944, 1.182327,
        1.163309, 1.14914, 1.139164, 1.132485, 1.128091, 1.125161, 1.123145,
        1.121704, 1.120638, 1.119826, 1.119192, 1.118689, 1.118287, 1.117965,
        1.117709, 1.117508, 1.117356, 1.117246, 1.117176, 1.117141, 1.117139,
        1.117169, 1.117229, 1.117318, 1.117436, 1.117581,
      ],
      [
        1.274952, 1.305238, 1.298007, 1.270042, 1.239201, 1.209605, 1.184993,
        1.165726, 1.151218, 1.140908, 1.133962, 1.129383, 1.126333, 1.124238,
        1.122747, 1.121647, 1.120811, 1.12016, 1.119646, 1.119235, 1.118908,
        1.118648, 1.118445, 1.118292, 1.118183, 1.118114, 1.118081, 1.118083,
        1.118117, 1.118182, 1.118277, 1.118401, 1.118554,
      ],
      [
        1.271038, 1.302611, 1.29821, 1.271005, 1.241193, 1.212195, 1.187647,
        1.168167, 1.153335, 1.142688, 1.135466, 1.130693, 1.127515, 1.125338,
        1.123793, 1.122658, 1.121797, 1.12113, 1.120603, 1.120185, 1.119851,
        1.119587, 1.119383, 1.119229, 1.119121, 1.119053, 1.119024, 1.119029,
        1.119068, 1.119138, 1.11924, 1.119371, 1.119531,
      ],
      [
        1.267127, 1.29972, 1.298384, 1.271887, 1.243068, 1.214707, 1.190282,
        1.170627, 1.155487, 1.144504, 1.136998, 1.132021, 1.128709, 1.126445,
        1.124844, 1.123671, 1.122785, 1.122099, 1.121561, 1.121134, 1.120795,
        1.120527, 1.120321, 1.120167, 1.120059, 1.119994, 1.119967, 1.119977,
        1.12002, 1.120097, 1.120205, 1.120343, 1.120512,
      ],
      [
        1.263275, 1.296587, 1.298524, 1.272692, 1.244826, 1.217134, 1.192888,
        1.1731, 1.157672, 1.146356, 1.138558, 1.133368, 1.129913, 1.127558,
        1.125897, 1.124685, 1.123772, 1.123069, 1.122518, 1.122082, 1.121737,
        1.121466, 1.121258, 1.121104, 1.120998, 1.120935, 1.120911, 1.120925,
        1.120974, 1.121057, 1.121172, 1.121318, 1.121495,
      ],
      [
        1.259529, 1.293247, 1.298627, 1.273428, 1.246471, 1.21947, 1.195456,
        1.175578, 1.159888, 1.148243, 1.140146, 1.134733, 1.131129, 1.128676,
        1.126953, 1.125699, 1.124759, 1.124037, 1.123473, 1.123028, 1.122678,
        1.122404, 1.122194, 1.12204, 1.121935, 1.121875, 1.121855, 1.121874,
        1.121929, 1.122018, 1.12214, 1.122295, 1.122482,
      ],
      [
        1.255924, 1.289742, 1.298682, 1.274097, 1.248004, 1.22171, 1.197978,
        1.178055, 1.162128, 1.150163, 1.141763, 1.136118, 1.132355, 1.1298,
        1.12801, 1.126714, 1.125745, 1.125003, 1.124426, 1.123972, 1.123616,
        1.123339, 1.123128, 1.122974, 1.122871, 1.122814, 1.122798, 1.122822,
        1.122883, 1.122979, 1.12311, 1.123273, 1.12347,
      ],
      [
        1.252487, 1.286121, 1.298678, 1.274706, 1.249429, 1.223849, 1.200446,
        1.180523, 1.16439, 1.152116, 1.143408, 1.137521, 1.133592, 1.130929,
        1.129069, 1.127727, 1.126728, 1.125966, 1.125375, 1.124913, 1.124551,
        1.124271, 1.124059, 1.123906, 1.123805, 1.123751, 1.12374, 1.123769,
        1.123836, 1.12394, 1.124079, 1.124252, 1.124459,
      ],
      [
        1.24923, 1.282434, 1.298592, 1.275258, 1.250751, 1.225883, 1.20285,
        1.182973, 1.166668, 1.154098, 1.14508, 1.138943, 1.134839, 1.132062,
        1.130128, 1.128738, 1.127707, 1.126925, 1.12632, 1.125849, 1.125482,
        1.125198, 1.124986, 1.124834, 1.124735, 1.124685, 1.124679, 1.124714,
        1.124788, 1.1249, 1.125048, 1.125231, 1.125449,
      ],
      [
        1.246159, 1.278731, 1.298395, 1.275757, 1.251973, 1.227812, 1.205185,
        1.185399, 1.168955, 1.156108, 1.146781, 1.140385, 1.136097, 1.133198,
        1.131187, 1.129746, 1.128683, 1.127879, 1.12726, 1.126779, 1.126407,
        1.126121, 1.125908, 1.125757, 1.125661, 1.125615, 1.125614, 1.125656,
        1.125738, 1.125858, 1.126016, 1.126209, 1.126438,
      ],
      [
        1.243267, 1.275054, 1.298044, 1.276207, 1.2531, 1.229634, 1.207442,
        1.18779, 1.171246, 1.158142, 1.148508, 1.141846, 1.137365, 1.134338,
        1.132245, 1.130751, 1.129653, 1.128826, 1.128193, 1.127703, 1.127325,
        1.127037, 1.126823, 1.126674, 1.126582, 1.12654, 1.126545, 1.126594,
        1.126684, 1.126813, 1.126981, 1.127186, 1.127427,
      ],
      [
        1.240542, 1.271437, 1.297482, 1.276609, 1.254137, 1.231348, 1.209616,
        1.19014, 1.173533, 1.160196, 1.150261, 1.143326, 1.138643, 1.135481,
        1.133301, 1.131751, 1.130617, 1.129767, 1.129118, 1.128619, 1.128236,
        1.127945, 1.127732, 1.127585, 1.127496, 1.127459, 1.127471, 1.127527,
        1.127626, 1.127765, 1.127943, 1.12816, 1.128414,
      ],
      [
        1.237966, 1.267905, 1.296638, 1.276965, 1.255088, 1.232956, 1.211701,
        1.192439, 1.175808, 1.162267, 1.152037, 1.144825, 1.139932, 1.136627,
        1.134354, 1.132745, 1.131573, 1.130699, 1.130035, 1.129526, 1.129138,
        1.128845, 1.128632, 1.128488, 1.128403, 1.128372, 1.12839, 1.128454,
        1.128562, 1.128712, 1.128901, 1.12913, 1.129397,
      ],
      [
        1.235518, 1.264472, 1.295435, 1.277275, 1.255959, 1.234459, 1.213694,
        1.19468, 1.178065, 1.164347, 1.153836, 1.146342, 1.141229, 1.137774,
        1.135403, 1.133733, 1.132521, 1.131621, 1.130941, 1.130423, 1.13003,
        1.129735, 1.129523, 1.129381, 1.129301, 1.129276, 1.129302, 1.129375,
        1.129492, 1.129653, 1.129855, 1.130096, 1.130378,
      ],
      [
        1.233171, 1.26114, 1.293804, 1.277537, 1.256754, 1.235859, 1.21559,
        1.196855, 1.180295, 1.166433, 1.155654, 1.147877, 1.142537, 1.138923,
        1.136449, 1.134712, 1.133459, 1.132533, 1.131836, 1.131309, 1.13091,
        1.130614, 1.130403, 1.130265, 1.13019, 1.130171, 1.130205, 1.130287,
        1.130415, 1.130587, 1.130802, 1.131057, 1.131353,
      ],
      [
        1.230899, 1.263163, 1.291705, 1.277748, 1.257477, 1.237158, 1.217387,
        1.198958, 1.182489, 1.168517, 1.157489, 1.149429, 1.143853, 1.140072,
        1.137489, 1.135682, 1.134385, 1.133432, 1.132718, 1.132181, 1.131778,
        1.131481, 1.131272, 1.131137, 1.131067, 1.131056, 1.131098, 1.13119,
        1.131329, 1.131514, 1.131741, 1.132011, 1.132322,
      ],
      [
        1.228674, 1.260518, 1.289155, 1.277898, 1.258131, 1.238361, 1.219083,
        1.200983, 1.184639, 1.170592, 1.159336, 1.150997, 1.145178, 1.141222,
        1.138523, 1.136643, 1.135299, 1.134317, 1.133586, 1.133039, 1.132631,
        1.132333, 1.132126, 1.131995, 1.131932, 1.131929, 1.13198, 1.132083,
        1.132234, 1.132431, 1.132673, 1.132958, 1.133285,
      ],
      [
        1.226468, 1.258008, 1.286229, 1.277975, 1.258721, 1.239469, 1.220678,
        1.202923, 1.186738, 1.172651, 1.161191, 1.152578, 1.146511, 1.142371,
        1.139549, 1.137591, 1.136199, 1.135187, 1.134438, 1.133881, 1.133469,
        1.13317, 1.132965, 1.13284, 1.132783, 1.132788, 1.13285, 1.132964,
        1.133127, 1.133338, 1.133594, 1.133895, 1.134239,
      ],
      [
        1.224253, 1.255611, 1.283045, 1.277959, 1.259247, 1.240486, 1.222172,
        1.204775, 1.188778, 1.174686, 1.163049, 1.154171, 1.147851, 1.143519,
        1.140568, 1.138527, 1.137084, 1.13604, 1.135273, 1.134705, 1.134289,
        1.13399, 1.133788, 1.133668, 1.133619, 1.133633, 1.133705, 1.133831,
        1.134008, 1.134233, 1.134505, 1.134822, 1.135184,
      ],
      [
        1.222004, 1.253297, 1.279735, 1.277821, 1.259712, 1.241417, 1.223564,
        1.206533, 1.19075, 1.176689, 1.164904, 1.155774, 1.149197, 1.144665,
        1.141577, 1.139449, 1.137951, 1.136874, 1.136088, 1.13551, 1.135089,
        1.134791, 1.134593, 1.134479, 1.134437, 1.134461, 1.134545, 1.134684,
        1.134874, 1.135115, 1.135403, 1.135738, 1.136118,
      ],
    ],
    muPaS: [
      [
        4.970513e-5, 5.324848e-5, 6.121074e-5, 6.928971e-5, 7.625718e-5,
        8.203451e-5, 8.675386e-5, 9.057257e-5, 9.361089e-5, 9.598719e-5,
        9.783002e-5, 9.925944e-5, 0.0001003729, 0.000101244, 0.0001019266,
        0.0001024607, 0.0001028757, 0.0001031941, 0.0001034331, 0.000103606,
        0.0001037236, 0.0001037944, 0.0001038253, 0.000103822, 0.0001037893,
        0.000103731, 0.0001036505, 0.0001035505, 0.0001034335, 0.0001033015,
        0.0001031562, 0.0001029991, 0.0001028316,
      ],
      [
        4.977236e-5, 5.327714e-5, 6.121506e-5, 6.932003e-5, 7.635385e-5,
        8.222515e-5, 8.704318e-5, 9.095412e-5, 9.407322e-5, 9.651545e-5,
        9.84086e-5, 9.987454e-5, 0.0001010135, 0.0001019018, 0.0001025954,
        0.0001031357, 0.0001035536, 0.0001038722, 0.0001041094, 0.0001042791,
        0.0001043923, 0.0001044577, 0.0001044826, 0.0001044728, 0.0001044331,
        0.0001043675, 0.0001042795, 0.0001041719, 0.0001040471, 0.0001039071,
        0.0001037538, 0.0001035886, 0.0001034129,
      ],
      [
        4.985109e-5, 5.331167e-5, 6.121901e-5, 6.934774e-5, 7.644415e-5,
        8.240713e-5, 8.732399e-5, 9.132887e-5, 9.453129e-5, 9.704225e-5,
        9.89882e-5, 0.0001004926, 0.0001016585, 0.0001025648, 0.00010327,
        0.000103817, 0.0001042379, 0.0001045568, 0.0001047922, 0.0001049585,
        0.0001050671, 0.0001051271, 0.0001051457, 0.0001051291, 0.0001050822,
        0.0001050091, 0.0001049133, 0.0001047977, 0.0001046648, 0.0001045166,
        0.000104355, 0.0001041815, 0.0001039974,
      ],
      [
        4.994259e-5, 5.335316e-5, 6.122262e-5, 6.937304e-5, 7.65283e-5,
        8.258042e-5, 8.759591e-5, 9.169624e-5, 9.498451e-5, 9.756703e-5,
        9.956838e-5, 0.0001011133, 0.0001023076, 0.000103233, 0.0001039504,
        0.0001045045, 0.0001049286, 0.0001052478, 0.0001054814, 0.0001056443,
        0.0001057481, 0.0001058023, 0.0001058145, 0.0001057909, 0.0001057366,
        0.0001056557, 0.0001055518, 0.000105428, 0.0001052866, 0.0001051299,
        0.0001049598, 0.0001047777, 0.000104585,
      ],
      [
        5.004804e-5, 5.340278e-5, 6.122595e-5, 6.939612e-5, 7.660656e-5,
        8.2745e-5, 8.785862e-5, 9.205571e-5, 9.543226e-5, 9.808923e-5,
        0.0001001487, 0.0001017363, 0.0001029606, 0.0001039063, 0.0001046365,
        0.0001051981, 0.0001056256, 0.0001059453, 0.000106177, 0.0001063362,
        0.0001064352, 0.0001064835, 0.000106489, 0.0001064581, 0.000106396,
        0.0001063071, 0.0001061949, 0.0001060625, 0.0001059125, 0.000105747,
        0.000105568, 0.000105377, 0.0001051754,
      ],
      [
        5.01686e-5, 5.346187e-5, 6.122905e-5, 6.941715e-5, 7.667916e-5,
        8.290091e-5, 8.811183e-5, 9.240675e-5, 9.587391e-5, 9.860825e-5,
        0.0001007286, 0.0001023613, 0.0001036173, 0.0001045843, 0.0001053283,
        0.0001058977, 0.000106329, 0.0001066491, 0.0001068789, 0.0001070344,
        0.0001071283, 0.0001071705, 0.0001071691, 0.0001071306, 0.0001070605,
        0.0001069632, 0.0001068424, 0.0001067012, 0.0001065422, 0.0001063677,
        0.0001061795, 0.0001059793, 0.0001057685,
      ],
      [
        5.030523e-5, 5.353177e-5, 6.123202e-5, 6.94363e-5, 7.674639e-5,
        8.304824e-5, 8.835529e-5, 9.274884e-5, 9.630881e-5, 9.912346e-5,
        0.0001013077, 0.0001029878, 0.0001042773, 0.000105267, 0.0001060254,
        0.0001066033, 0.0001070385, 0.0001073592, 0.0001075871, 0.0001077388,
        0.0001078274, 0.0001078632, 0.0001078546, 0.0001078084, 0.00010773,
        0.0001076241, 0.0001074943, 0.000107344, 0.0001071757, 0.0001069918,
        0.0001067942, 0.0001065845, 0.0001063641,
      ],
      [
        5.045874e-5, 5.361395e-5, 6.123491e-5, 6.945371e-5, 7.68085e-5,
        8.318708e-5, 8.858879e-5, 9.308149e-5, 9.673631e-5, 9.963419e-5,
        0.0001018854, 0.0001036154, 0.0001049404, 0.0001059541, 0.0001067279,
        0.0001073148, 0.0001077541, 0.0001080755, 0.0001083015, 0.0001084492,
        0.0001085324, 0.0001085617, 0.0001085457, 0.0001084913, 0.0001084044,
        0.0001082894, 0.0001081505, 0.0001079907, 0.0001078128, 0.0001076192,
        0.0001074118, 0.0001071923, 0.0001069621,
      ],
      [
        5.062975e-5, 5.370991e-5, 6.123786e-5, 6.946954e-5, 7.68658e-5,
        8.331761e-5, 8.881217e-5, 9.340424e-5, 9.715574e-5, 0.0001001398,
        0.0001024609, 0.0001042438, 0.0001056063, 0.0001066455, 0.0001074356,
        0.000108032, 0.0001084758, 0.000108798, 0.000109022, 0.0001091657,
        0.0001092432, 0.0001092657, 0.000109242, 0.0001091793, 0.0001090834,
        0.0001089593, 0.0001088107, 0.0001086412, 0.0001084534, 0.0001082497,
        0.0001080322, 0.0001078025, 0.0001075621,
      ],
      [
        5.081864e-5, 5.382108e-5, 6.124106e-5, 6.948393e-5, 7.691852e-5,
        8.344e-5, 8.902531e-5, 9.371663e-5, 9.756646e-5, 0.0001006394,
        0.0001030338, 0.0001048723, 0.0001062745, 0.0001073407, 0.0001081482,
        0.0001087547, 0.0001092035, 0.0001095266, 0.0001097486, 0.0001098881,
        0.0001099598, 0.0001099753, 0.0001099436, 0.0001098722, 0.0001097672,
        0.0001096334, 0.0001094749, 0.0001092952, 0.0001090972, 0.0001088832,
        0.0001086552, 0.000108415, 0.0001081642,
      ],
      [
        5.102562e-5, 5.394882e-5, 6.124475e-5, 6.949701e-5, 7.696702e-5,
        8.355449e-5, 8.922816e-5, 9.401827e-5, 9.79678e-5, 0.0001011325,
        0.0001036034, 0.0001055004, 0.0001069447, 0.0001080396, 0.0001088655,
        0.0001094829, 0.0001099369, 0.0001102611, 0.0001104811, 0.0001106164,
        0.0001106821, 0.0001106903, 0.0001106503, 0.0001105699, 0.0001104553,
        0.0001103116, 0.0001101429, 0.0001099528, 0.0001097441, 0.0001095193,
        0.0001092806, 0.0001090296, 0.0001087679,
      ],
      [
        5.125073e-5, 5.409432e-5, 6.124927e-5, 6.950891e-5, 7.70115e-5,
        8.366134e-5, 8.94207e-5, 9.430882e-5, 9.835915e-5, 0.0001016181,
        0.0001041688, 0.0001061275, 0.0001076164, 0.0001087418, 0.0001095874,
        0.0001102164, 0.0001106761, 0.0001110015, 0.0001112195, 0.0001113504,
        0.0001114099, 0.0001114105, 0.000111362, 0.0001112724, 0.0001111479,
        0.0001109938, 0.0001108145, 0.0001106136, 0.0001103939, 0.000110158,
        0.0001099081, 0.000109646, 0.000109373,
      ],
      [
        5.149385e-5, 5.425858e-5, 6.125516e-5, 6.951976e-5, 7.705227e-5,
        8.376081e-5, 8.960298e-5, 9.458796e-5, 9.873988e-5, 0.0001020956,
        0.0001047294, 0.0001067531, 0.0001082893, 0.000109447, 0.0001103136,
        0.000110955, 0.0001114207, 0.0001117477, 0.0001119637, 0.00011209,
        0.0001121432, 0.000112136, 0.0001120786, 0.0001119793, 0.0001118446,
        0.0001116799, 0.0001114896, 0.0001112774, 0.0001110464, 0.000110799,
        0.0001105376, 0.0001102638, 0.0001099793,
      ],
      [
        5.175463e-5, 5.444246e-5, 6.126321e-5, 6.952969e-5, 7.708957e-5,
        8.385323e-5, 8.97751e-5, 9.485546e-5, 9.910941e-5, 0.0001025642,
        0.0001052844, 0.0001073764, 0.0001089626, 0.0001101549, 0.0001110438,
        0.0001116985, 0.0001121708, 0.0001124994, 0.0001127135, 0.0001128352,
        0.0001128817, 0.0001128665, 0.0001127999, 0.0001126906, 0.0001125453,
        0.0001123695, 0.0001121679, 0.0001119441, 0.0001117013, 0.0001114421,
        0.0001111687, 0.000110883, 0.0001105865,
      ],
      [
        5.20327e-5, 5.464647e-5, 6.127454e-5, 6.953882e-5, 7.712367e-5,
        8.39389e-5, 8.99372e-5, 9.511112e-5, 9.94672e-5, 0.000103023,
        0.0001058329, 0.0001079968, 0.000109636, 0.0001108649, 0.0001117777,
        0.0001124466, 0.000112926, 0.0001132565, 0.0001134687, 0.0001135857,
        0.0001136255, 0.0001136018, 0.0001135258, 0.0001134061, 0.0001132498,
        0.0001130626, 0.0001128491, 0.0001126133, 0.0001123584, 0.0001120869,
        0.0001118012, 0.0001115031, 0.0001111943,
      ],
      [
        5.232756e-5, 5.487092e-5, 6.12908e-5, 6.954734e-5, 7.715481e-5,
        8.401815e-5, 9.008948e-5, 9.535482e-5, 9.981275e-5, 0.0001034713,
        0.000106374, 0.0001086134, 0.0001103087, 0.0001115767, 0.000112515,
        0.0001131991, 0.0001136863, 0.000114019, 0.0001142293, 0.0001143415,
        0.0001143742, 0.0001143419, 0.000114256, 0.0001141256, 0.0001139579,
        0.0001137588, 0.0001135332, 0.0001132849, 0.0001130174, 0.0001127332,
        0.0001124348, 0.0001121239, 0.0001118023,
      ],
      [
        5.263864e-5, 5.511586e-5, 6.131434e-5, 6.955537e-5, 7.718323e-5,
        8.409132e-5, 9.023216e-5, 9.558648e-5, 0.0001001456, 0.0001039083,
        0.000106907, 0.0001092254, 0.0001109803, 0.0001122898, 0.0001132553,
        0.0001139558, 0.0001144513, 0.0001147865, 0.000114995, 0.0001151023,
        0.0001151277, 0.0001150865, 0.0001149904, 0.0001148488, 0.0001146693,
        0.000114458, 0.0001142197, 0.0001139586, 0.000113678, 0.0001133807,
        0.0001130691, 0.0001127451, 0.0001124103,
      ],
      [
        5.296533e-5, 5.53811e-5, 6.134837e-5, 6.956317e-5, 7.720916e-5,
        8.415875e-5, 9.036552e-5, 9.580612e-5, 0.0001004654, 0.0001043333,
        0.0001074309, 0.0001098321, 0.0001116498, 0.0001130037, 0.0001139983,
        0.0001147164, 0.0001152209, 0.0001155589, 0.0001157657, 0.0001158679,
        0.0001158859, 0.0001158354, 0.0001157287, 0.0001155757, 0.0001153839,
        0.0001151598, 0.0001149085, 0.0001146341, 0.00011434, 0.0001140291,
        0.0001137039, 0.0001133662, 0.0001130178,
      ],
      [
        5.330699e-5, 5.56663e-5, 6.139715e-5, 6.957103e-5, 7.723282e-5,
        8.422075e-5, 9.048987e-5, 9.60138e-5, 0.0001007718, 0.0001047456,
        0.0001079448, 0.0001104325, 0.0001123167, 0.0001137177, 0.0001147435,
        0.0001154805, 0.0001159948, 0.000116336, 0.0001165411, 0.0001166382,
        0.0001166485, 0.0001165884, 0.0001164708, 0.0001163058, 0.0001161014,
        0.0001158641, 0.0001155992, 0.000115311, 0.0001150029, 0.000114678,
        0.0001143387, 0.0001139869, 0.0001136245,
      ],
      [
        5.366291e-5, 5.597089e-5, 6.146595e-5, 6.957935e-5, 7.725445e-5,
        8.427772e-5, 9.060556e-5, 9.620963e-5, 0.0001010646, 0.0001051446,
        0.0001084477, 0.0001110256, 0.0001129801, 0.0001144314, 0.0001154904,
        0.0001162477, 0.0001167727, 0.0001171175, 0.0001173211, 0.0001174129,
        0.0001174153, 0.0001173453, 0.0001172164, 0.0001170389, 0.0001168214,
        0.0001165705, 0.0001162915, 0.000115989, 0.0001156665, 0.0001153271,
        0.0001149732, 0.0001146069, 0.00011423,
      ],
      [
        5.40324e-5, 5.631582e-5, 6.156081e-5, 6.958871e-5, 7.727428e-5,
        8.432998e-5, 9.071292e-5, 9.639381e-5, 0.0001013436, 0.0001055296,
        0.0001089388, 0.0001116106, 0.0001136392, 0.0001151439, 0.0001162386,
        0.0001170178, 0.0001175543, 0.0001179031, 0.0001181054, 0.0001181919,
        0.000118186, 0.0001181058, 0.0001179651, 0.0001177749, 0.0001175437,
        0.0001172786, 0.0001169851, 0.0001166678, 0.0001163304, 0.0001159759,
        0.000115607, 0.0001152256, 0.0001148337,
      ],
      [
        5.441475e-5, 5.668437e-5, 6.168791e-5, 6.959994e-5, 7.729254e-5,
        8.437786e-5, 9.081236e-5, 9.656657e-5, 0.0001016086, 0.0001059001,
        0.0001094171, 0.0001121863, 0.000114293, 0.0001158546, 0.0001169876,
        0.0001177902, 0.0001183393, 0.0001186927, 0.0001188937, 0.0001189748,
        0.0001189605, 0.0001188697, 0.0001187168, 0.0001185132, 0.000118268,
        0.0001179882, 0.0001176796, 0.000117347, 0.0001169941, 0.0001166241,
        0.0001162396, 0.0001158428, 0.0001154354,
      ],
      [
        5.480925e-5, 5.70745e-5, 6.185274e-5, 6.961424e-5, 7.730953e-5,
        8.44217e-5, 9.090427e-5, 9.672822e-5, 0.0001018598, 0.0001062556,
        0.0001098819, 0.0001127518, 0.0001149407, 0.0001165628, 0.0001177366,
        0.0001185646, 0.0001191273, 0.0001194858, 0.0001196857, 0.0001197613,
        0.0001197384, 0.0001196366, 0.0001194711, 0.0001192537, 0.0001189938,
        0.0001186988, 0.0001183746, 0.0001180261, 0.0001176572, 0.0001172712,
        0.0001168706, 0.0001164578, 0.0001160345,
      ],
      [
        5.521519e-5, 5.748544e-5, 6.205931e-5, 6.963337e-5, 7.732558e-5,
        8.44618e-5, 9.098906e-5, 9.687907e-5, 0.0001020971, 0.0001065958,
        0.0001103322, 0.000113306, 0.0001155812, 0.0001172675, 0.0001184852,
        0.0001193404, 0.0001199179, 0.0001202821, 0.0001204812, 0.0001205513,
        0.0001205194, 0.0001204062, 0.0001202276, 0.0001199959, 0.0001197208,
        0.00011941, 0.0001190697, 0.0001187048, 0.0001183194, 0.0001179167,
        0.0001174996, 0.0001170702, 0.0001166305,
      ],
      [
        5.563184e-5, 5.791641e-5, 6.230969e-5, 6.96598e-5, 7.73411e-5,
        8.449854e-5, 9.106716e-5, 9.701952e-5, 0.0001023207, 0.0001069203,
        0.0001107674, 0.0001138479, 0.0001162134, 0.000117968, 0.0001192326,
        0.0001201172, 0.0001207108, 0.0001210813, 0.0001212798, 0.0001213442,
        0.0001213031, 0.0001211782, 0.000120986, 0.0001207395, 0.0001204487,
        0.0001201215, 0.0001197645, 0.0001193826, 0.00011898, 0.0001185602,
        0.000118126, 0.0001176795, 0.0001172229,
      ],
    ],
    cstar: [
      [
        1328.075, 1379.002, 1482.219, 1573.118, 1642.114, 1691.608, 1723.722,
        1742.047, 1749.903, 1750.017, 1744.85, 1736.426, 1726.164, 1714.961,
        1703.358, 1691.676, 1680.101, 1668.743, 1657.663, 1646.895, 1636.45,
        1626.331, 1616.536, 1607.054, 1597.874, 1588.984, 1580.37, 1572.018,
        1563.915, 1556.049, 1548.405, 1540.972, 1533.737,
      ],
      [
        1330.405, 1380.171, 1482.205, 1573.073, 1642.219, 1692.3, 1725.292,
        1744.508, 1753.132, 1753.838, 1749.075, 1740.901, 1730.783, 1719.655,
        1708.083, 1696.402, 1684.812, 1673.426, 1662.311, 1651.501, 1641.013,
        1630.848, 1621.003, 1611.471, 1602.241, 1593.299, 1584.633, 1576.23,
        1568.075, 1560.155, 1552.458, 1544.972, 1537.685,
      ],
      [
        1332.918, 1381.516, 1482.194, 1573.031, 1642.304, 1692.923, 1726.76,
        1746.862, 1756.27, 1757.59, 1753.254, 1745.348, 1735.384, 1724.338,
        1712.8, 1701.125, 1689.521, 1678.109, 1666.959, 1656.108, 1645.575,
        1635.362, 1625.47, 1615.887, 1606.605, 1597.612, 1588.893, 1580.437,
        1572.228, 1564.255, 1556.505, 1548.965, 1541.623,
      ],
      [
        1335.606, 1383.047, 1482.186, 1572.992, 1642.372, 1693.482, 1728.127,
        1749.108, 1759.312, 1761.269, 1757.382, 1749.762, 1739.965, 1729.008,
        1717.511, 1705.844, 1694.227, 1682.79, 1671.606, 1660.715, 1650.136,
        1639.876, 1629.933, 1620.301, 1610.967, 1601.921, 1593.149, 1584.638,
        1576.376, 1568.348, 1560.543, 1552.947, 1545.55,
      ],
      [
        1338.456, 1384.775, 1482.181, 1572.955, 1642.427, 1693.981, 1729.397,
        1751.245, 1762.255, 1764.871, 1761.456, 1754.14, 1744.523, 1733.663,
        1722.211, 1710.556, 1698.929, 1687.467, 1676.249, 1665.318, 1654.695,
        1644.387, 1634.395, 1624.71, 1615.323, 1606.224, 1597.398, 1588.833,
        1580.515, 1572.432, 1564.57, 1556.919, 1549.465,
      ],
      [
        1341.457, 1386.705, 1482.181, 1572.922, 1642.471, 1694.427, 1730.571,
        1753.272, 1765.095, 1768.39, 1765.471, 1758.478, 1749.054, 1738.301,
        1726.9, 1715.259, 1703.625, 1692.14, 1680.889, 1669.918, 1659.25,
        1648.894, 1638.851, 1629.114, 1619.674, 1610.52, 1601.639, 1593.018,
        1584.644, 1576.505, 1568.586, 1560.877, 1553.366,
      ],
      [
        1344.596, 1388.839, 1482.187, 1572.891, 1642.505, 1694.822, 1731.654,
        1755.188, 1767.829, 1771.822, 1769.423, 1762.771, 1753.556, 1742.919,
        1731.575, 1719.953, 1708.313, 1696.807, 1685.523, 1674.512, 1663.8,
        1653.396, 1643.302, 1633.512, 1624.018, 1614.808, 1605.871, 1597.194,
        1588.763, 1580.566, 1572.589, 1564.822, 1557.251,
      ],
      [
        1347.858, 1391.174, 1482.202, 1572.862, 1642.529, 1695.171, 1732.649,
        1756.993, 1770.453, 1775.161, 1773.305, 1767.019, 1758.026, 1747.516,
        1736.234, 1724.636, 1712.993, 1701.466, 1690.151, 1679.101, 1668.343,
        1657.89, 1647.745, 1637.902, 1628.353, 1619.087, 1610.093, 1601.358,
        1592.869, 1584.613, 1576.577, 1568.75, 1561.119,
      ],
      [
        1351.231, 1393.704, 1482.228, 1572.836, 1642.548, 1695.48, 1733.559,
        1758.689, 1772.965, 1778.404, 1777.115, 1771.214, 1762.461, 1752.087,
        1740.876, 1729.305, 1717.661, 1706.116, 1694.77, 1683.681, 1672.878,
        1662.377, 1652.179, 1642.282, 1632.677, 1623.355, 1614.303, 1605.509,
        1596.961, 1588.645, 1580.549, 1572.661, 1564.969,
      ],
      [
        1354.702, 1396.422, 1482.273, 1572.812, 1642.562, 1695.751, 1734.391,
        1760.278, 1775.363, 1781.546, 1780.847, 1775.354, 1766.857, 1756.632,
        1745.498, 1733.959, 1722.317, 1710.755, 1699.379, 1688.251, 1677.404,
        1666.853, 1656.603, 1646.652, 1636.991, 1627.61, 1618.5, 1609.646,
        1601.037, 1592.66, 1584.502, 1576.551, 1568.797,
      ],
      [
        1358.259, 1399.315, 1482.346, 1572.791, 1642.57, 1695.99, 1735.147,
        1761.76, 1777.645, 1784.583, 1784.495, 1779.434, 1771.211, 1761.146,
        1750.097, 1738.595, 1726.959, 1715.382, 1703.977, 1692.811, 1681.919,
        1671.319, 1661.016, 1651.009, 1641.29, 1631.852, 1622.681, 1613.767,
        1605.096, 1596.657, 1588.436, 1580.422, 1572.602,
      ],
      [
        1361.89, 1402.369, 1482.458, 1572.773, 1642.576, 1696.2, 1735.832,
        1763.139, 1779.81, 1787.51, 1788.055, 1783.449, 1775.519, 1765.627,
        1754.672, 1743.212, 1731.585, 1719.995, 1708.562, 1697.358, 1686.421,
        1675.771, 1665.415, 1655.352, 1645.575, 1636.077, 1626.846, 1617.869,
        1609.136, 1600.633, 1592.348, 1584.269, 1576.384,
      ],
      [
        1365.585, 1405.571, 1482.629, 1572.755, 1642.579, 1696.383, 1736.453,
        1764.418, 1781.859, 1790.324, 1791.522, 1787.395, 1779.777, 1770.073,
        1759.22, 1747.808, 1736.193, 1724.591, 1713.132, 1701.89, 1690.909,
        1680.209, 1669.799, 1659.679, 1649.844, 1640.285, 1630.991, 1621.952,
        1613.155, 1604.587, 1596.236, 1588.091, 1580.139,
      ],
      [
        1369.334, 1408.904, 1482.884, 1572.742, 1642.58, 1696.543, 1737.013,
        1765.6, 1783.791, 1793.023, 1794.891, 1791.266, 1783.981, 1774.479,
        1763.739, 1752.38, 1740.781, 1729.17, 1717.686, 1706.407, 1695.381,
        1684.63, 1674.166, 1663.989, 1654.094, 1644.473, 1635.117, 1626.013,
        1617.151, 1608.516, 1600.099, 1591.886, 1583.865,
      ],
      [
        1373.125, 1412.354, 1483.258, 1572.732, 1642.58, 1696.683, 1737.517,
        1766.688, 1785.607, 1795.603, 1798.158, 1795.058, 1788.127, 1778.842,
        1768.225, 1756.926, 1745.347, 1733.73, 1722.221, 1710.906, 1699.835,
        1689.034, 1678.515, 1668.279, 1658.324, 1648.641, 1639.22, 1630.051,
        1621.122, 1612.42, 1603.934, 1595.652, 1587.561,
      ],
      [
        1376.952, 1415.905, 1483.796, 1572.727, 1642.577, 1696.806, 1737.97,
        1767.689, 1787.308, 1798.063, 1801.317, 1798.766, 1792.211, 1783.159,
        1772.675, 1761.444, 1749.89, 1738.268, 1726.736, 1715.386, 1704.27,
        1693.419, 1682.844, 1672.549, 1662.532, 1652.785, 1643.299, 1634.063,
        1625.066, 1616.296, 1607.74, 1599.386, 1591.225,
      ],
      [
        1380.802, 1419.542, 1484.551, 1572.728, 1642.574, 1696.913, 1738.376,
        1768.606, 1788.897, 1800.4, 1804.364, 1802.384, 1796.227, 1787.427,
        1777.088, 1765.932, 1754.406, 1742.783, 1731.23, 1719.845, 1708.685,
        1697.782, 1687.151, 1676.796, 1666.716, 1656.905, 1647.352, 1638.048,
        1628.982, 1620.141, 1611.513, 1603.088, 1594.853,
      ],
      [
        1384.669, 1423.252, 1485.579, 1572.736, 1642.572, 1697.005, 1738.74,
        1769.445, 1790.377, 1802.614, 1807.295, 1805.907, 1800.173, 1791.641,
        1781.46, 1770.387, 1758.894, 1747.273, 1735.7, 1724.28, 1713.076,
        1702.121, 1691.433, 1681.018, 1670.875, 1660.997, 1651.377, 1642.004,
        1632.867, 1623.954, 1615.253, 1606.754, 1598.444,
      ],
      [
        1388.542, 1427.02, 1486.93, 1572.755, 1642.569, 1697.086, 1739.065,
        1770.209, 1791.752, 1804.705, 1810.107, 1809.332, 1804.042, 1795.798,
        1785.788, 1774.806, 1763.352, 1751.736, 1740.144, 1728.691, 1717.442,
        1706.436, 1695.691, 1685.214, 1675.006, 1665.061, 1655.372, 1645.928,
        1636.718, 1627.732, 1618.957, 1610.382, 1601.996,
      ],
      [
        1392.414, 1430.833, 1488.633, 1572.791, 1642.566, 1697.156, 1739.355,
        1770.904, 1793.024, 1806.674, 1812.798, 1812.652, 1807.83, 1799.894,
        1790.069, 1779.186, 1767.777, 1756.169, 1744.561, 1733.075, 1721.782,
        1710.723, 1699.92, 1689.381, 1679.107, 1669.094, 1659.334, 1649.818,
        1640.535, 1631.473, 1622.622, 1613.97, 1605.506,
      ],
      [
        1396.274, 1434.649, 1490.698, 1572.851, 1642.564, 1697.219, 1739.613,
        1771.536, 1794.199, 1808.521, 1815.363, 1815.862, 1811.531, 1803.925,
        1794.299, 1783.526, 1772.167, 1760.57, 1748.948, 1737.43, 1726.093,
        1714.982, 1704.12, 1693.517, 1683.176, 1673.093, 1663.262, 1653.672,
        1644.313, 1635.175, 1626.247, 1617.516, 1608.972,
      ],
      [
        1400.113, 1438.405, 1493.106, 1572.947, 1642.565, 1697.27, 1739.842,
        1772.105, 1795.281, 1810.25, 1817.801, 1818.959, 1815.142, 1807.886,
        1798.475, 1787.822, 1776.52, 1764.938, 1753.303, 1741.754, 1730.373,
        1719.209, 1708.287, 1697.62, 1687.212, 1677.058, 1667.152, 1657.487,
        1648.052, 1638.836, 1629.828, 1621.017, 1612.391,
      ],
      [
        1403.923, 1442.091, 1495.819, 1573.094, 1642.566, 1697.318, 1740.047,
        1772.622, 1796.272, 1811.862, 1820.112, 1821.937, 1818.656, 1811.772,
        1802.593, 1792.072, 1780.833, 1769.27, 1757.625, 1746.046, 1734.621,
        1723.403, 1712.421, 1701.689, 1691.211, 1680.984, 1671.004, 1661.262,
        1651.749, 1642.453, 1633.363, 1624.47, 1615.761,
      ],
      [
        1407.695, 1445.689, 1498.788, 1573.314, 1642.572, 1697.359, 1740.229,
        1773.088, 1797.181, 1813.361, 1822.295, 1824.794, 1822.07, 1815.58,
        1806.651, 1796.272, 1785.104, 1773.564, 1761.911, 1750.302, 1738.834,
        1727.562, 1716.519, 1705.72, 1695.171, 1684.871, 1674.815, 1664.994,
        1655.4, 1646.023, 1636.851, 1627.873, 1619.08,
      ],
      [
        1411.42, 1449.186, 1501.965, 1573.636, 1642.583, 1697.396, 1740.391,
        1773.507, 1798.012, 1814.751, 1824.351, 1827.525, 1825.377, 1819.304,
        1810.643, 1800.419, 1789.33, 1777.818, 1766.158, 1754.522, 1743.01,
        1731.684, 1720.578, 1709.712, 1699.091, 1688.715, 1678.581, 1668.681,
        1659.005, 1649.544, 1640.287, 1631.224, 1622.344,
      ],
    ],
  },
  "lox-ch4": {
    pcGridPa: [
      200000.0, 246433.9, 303648.4, 374146.3, 461011.6, 568044.5, 699927.1,
      862428.9, 1062659.0, 1309376.0, 1613373.0, 1987949.0, 2449490.0,
      3018187.0, 3718918.0, 4582337.0, 5646216.0, 6957096.0, 8572321.0,
      10562550.0, 13014860.0, 16036510.0, 19759700.0, 24347300.0, 30000000.0,
    ],
    ofGrid: [
      1.0, 1.125, 1.25, 1.375, 1.5, 1.625, 1.75, 1.875, 2.0, 2.125, 2.25, 2.375,
      2.5, 2.625, 2.75, 2.875, 3.0, 3.125, 3.25, 3.375, 3.5, 3.625, 3.75, 3.875,
      4.0, 4.125, 4.25, 4.375, 4.5, 4.625, 4.75, 4.875, 5.0,
    ],
    t0: [
      [
        1033.974, 1063.742, 1157.306, 1407.94, 1667.403, 1909.474, 2131.476,
        2328.663, 2497.36, 2637.388, 2751.179, 2841.953, 2912.917, 2967.22,
        3007.944, 3037.933, 3059.619, 3074.951, 3085.427, 3092.172, 3096.029,
        3097.628, 3097.44, 3095.826, 3093.062, 3089.361, 3084.89, 3079.781,
        3074.139, 3068.05, 3061.581, 3054.789, 3047.719,
      ],
      [
        1045.697, 1076.303, 1164.566, 1408.049, 1667.44, 1909.722, 2132.496,
        2331.402, 2502.752, 2645.951, 2762.996, 2856.827, 2930.497, 2987.069,
        3029.603, 3060.977, 3083.684, 3099.745, 3110.723, 3117.799, 3121.854,
        3123.551, 3123.386, 3121.735, 3118.888, 3115.067, 3110.449, 3105.169,
        3099.338, 3093.045, 3086.359, 3079.34, 3072.036,
      ],
      [
        1057.713, 1089.188, 1172.818, 1408.212, 1667.474, 1909.947, 2133.425,
        2333.934, 2507.821, 2654.133, 2774.437, 2871.381, 2947.84, 3006.777,
        3051.214, 3084.051, 3107.842, 3124.68, 3136.195, 3143.623, 3147.893,
        3149.697, 3149.559, 3147.873, 3144.941, 3140.999, 3136.228, 3130.772,
        3124.746, 3118.241, 3111.331, 3104.078, 3096.529,
      ],
      [
        1070.03, 1102.403, 1182.007, 1408.457, 1667.508, 1910.15, 2134.271,
        2336.269, 2512.575, 2661.929, 2785.488, 2885.591, 2964.92, 3026.318,
        3072.754, 3107.138, 3132.08, 3149.744, 3161.832, 3169.639, 3174.139,
        3176.061, 3175.955, 3174.235, 3171.219, 3167.151, 3162.224, 3156.586,
        3150.357, 3143.634, 3136.492, 3128.995, 3121.194,
      ],
      [
        1082.653, 1115.955, 1192.074, 1408.823, 1667.543, 1910.334, 2135.04,
        2338.42, 2517.023, 2669.339, 2796.135, 2899.435, 2981.711, 3045.665,
        3094.197, 3130.215, 3156.379, 3174.925, 3187.624, 3195.837, 3200.585,
        3202.637, 3202.569, 3200.817, 3197.715, 3193.52, 3188.43, 3182.605,
        3176.167, 3169.217, 3161.835, 3154.086, 3146.023,
      ],
      [
        1095.588, 1129.85, 1202.96, 1409.37, 1667.58, 1910.5, 2135.74, 2340.397,
        2521.174, 2676.362, 2806.367, 2912.894, 2998.186, 3064.791, 3115.519,
        3153.263, 3180.724, 3200.208, 3213.562, 3222.209, 3227.226, 3229.418,
        3229.395, 3227.614, 3224.425, 3220.098, 3214.843, 3208.823, 3202.169,
        3194.985, 3187.354, 3179.344, 3171.01,
      ],
      [
        1108.839, 1144.096, 1214.612, 1410.181, 1667.623, 1910.651, 2136.375,
        2342.212, 2525.039, 2683.003, 2816.176, 2925.947, 3014.319, 3083.666,
        3136.691, 3176.256, 3205.094, 3225.579, 3239.632, 3248.746, 3254.051,
        3256.398, 3256.426, 3254.618, 3251.343, 3246.882, 3241.456, 3235.236,
        3228.358, 3220.931, 3213.042, 3204.761, 3196.146,
      ],
      [
        1122.412, 1158.7, 1226.984, 1411.37, 1667.676, 1910.789, 2136.951,
        2343.875, 2528.63, 2689.264, 2825.554, 2938.576, 3030.084, 3102.262,
        3157.687, 3199.172, 3229.47, 3251.021, 3265.822, 3275.437, 3281.054,
        3283.569, 3283.657, 3281.825, 3278.462, 3273.864, 3268.262, 3261.835,
        3254.726, 3247.048, 3238.891, 3230.33, 3221.424,
      ],
      [
        1136.31, 1173.669, 1240.039, 1413.087, 1667.746, 1910.915, 2137.474,
        2345.396, 2531.958, 2695.154, 2834.497, 2950.765, 3045.456, 3120.549,
        3178.476, 3221.982, 3253.83, 3276.517, 3292.118, 3302.27, 3308.223,
        3310.922, 3311.078, 3309.227, 3305.776, 3301.038, 3295.254, 3288.614,
        3281.265, 3273.327, 3264.894, 3256.042, 3246.834,
      ],
      [
        1150.537, 1189.009, 1253.746, 1415.519, 1667.841, 1911.03, 2137.949,
        2346.787, 2535.038, 2700.679, 2843.002, 2962.499, 3060.41, 3138.498,
        3199.028, 3244.659, 3278.15, 3302.048, 3318.505, 3329.233, 3335.549,
        3338.449, 3338.683, 3336.816, 3333.277, 3328.396, 3322.426, 3315.565,
        3307.969, 3299.761, 3291.041, 3281.888, 3272.367,
      ],
      [
        1165.096, 1204.729, 1268.079, 1418.876, 1667.977, 1911.139, 2138.379,
        2348.057, 2537.881, 2705.85, 2851.069, 2973.766, 3074.923, 3156.077,
        3219.311, 3267.175, 3302.405, 3327.593, 3344.966, 3356.314, 3363.021,
        3366.14, 3366.462, 3364.583, 3360.957, 3355.93, 3349.768, 3342.679,
        3334.827, 3326.34, 3317.323, 3307.858, 3298.012,
      ],
      [
        1179.988, 1220.834, 1283.018, 1423.374, 1668.174, 1911.242, 2138.769,
        2349.214, 2540.502, 2710.677, 2858.701, 2984.557, 3088.975, 3173.259,
        3239.293, 3289.498, 3326.568, 3353.131, 3371.483, 3383.497, 3390.627,
        3393.985, 3394.406, 3392.521, 3388.808, 3383.631, 3377.272, 3369.948,
        3361.831, 3353.056, 3343.73, 3333.941, 3323.759,
      ],
      [
        1195.217, 1237.332, 1298.548, 1429.2, 1668.465, 1911.344, 2139.123,
        2350.268, 2542.912, 2715.173, 2865.901, 2994.863, 3102.545, 3190.013,
        3258.941, 3311.595, 3350.612, 3378.638, 3398.039, 3410.767, 3418.354,
        3421.972, 3422.505, 3420.619, 3416.82, 3411.491, 3404.929, 3397.362,
        3388.971, 3379.896, 3370.251, 3360.126, 3349.596,
      ],
      [
        1210.781, 1254.229, 1314.657, 1436.487, 1668.895, 1911.449, 2139.444,
        2351.228, 2545.126, 2719.351, 2872.677, 3004.679, 3115.615, 3206.313,
        3278.221, 3333.435, 3374.506, 3404.089, 3424.612, 3438.108, 3446.188,
        3450.09, 3450.748, 3448.868, 3444.983, 3439.5, 3432.729, 3424.911,
        3416.235, 3406.85, 3396.874, 3386.402, 3375.51,
      ],
      [
        1226.681, 1271.532, 1331.336, 1445.301, 1669.533, 1911.563, 2139.738,
        2352.101, 2547.156, 2723.225, 2879.037, 3014.005, 3128.171, 3222.131,
        3297.1, 3354.981, 3398.219, 3429.458, 3451.18, 3465.502, 3474.115,
        3478.326, 3479.124, 3477.256, 3473.287, 3467.646, 3460.66, 3452.583,
        3443.613, 3433.907, 3423.588, 3412.754, 3401.487,
      ],
      [
        1243.111, 1289.245, 1348.578, 1455.642, 1670.477, 1911.696, 2140.007,
        2352.894, 2549.015, 2726.81, 2884.991, 3022.84, 3140.201, 3237.444,
        3315.546, 3376.199, 3421.718, 3454.716, 3477.722, 3492.93, 3502.119,
        3506.666, 3507.62, 3505.773, 3501.721, 3495.919, 3488.712, 3480.367,
        3471.093, 3461.054, 3450.378, 3439.171, 3427.515,
      ],
      [
        1260.487, 1307.374, 1366.377, 1467.46, 1671.863, 1911.861, 2140.257,
        2353.615, 2550.715, 2730.12, 2890.552, 3031.187, 3151.694, 3252.229,
        3333.526, 3397.053, 3444.969, 3479.834, 3504.211, 3520.373, 3530.182,
        3535.095, 3536.223, 3534.405, 3530.273, 3524.307, 3516.872, 3508.25,
        3498.661, 3488.276, 3477.232, 3465.636, 3453.577,
      ],
      [
        1278.225, 1325.924, 1384.729, 1480.68, 1673.871, 1912.078, 2140.492,
        2354.271, 2552.267, 2733.171, 2895.733, 3039.052, 3162.645, 3266.465,
        3351.01, 3417.506, 3467.935, 3504.78, 3530.622, 3547.808, 3558.288,
        3563.6, 3564.921, 3563.14, 3558.93, 3552.796, 3545.127, 3536.22,
        3526.304, 3515.561, 3504.134, 3492.135, 3479.658,
      ],
      [
        1296.327, 1344.898, 1403.632, 1495.209, 1676.731, 1912.378, 2140.718,
        2354.867, 2553.684, 2735.978, 2900.549, 3046.444, 3173.05, 3280.136,
        3367.967, 3437.522, 3490.579, 3529.52, 3556.928, 3575.213, 3586.417,
        3592.163, 3593.697, 3591.965, 3587.678, 3581.375, 3573.465, 3564.262,
        3554.008, 3542.893, 3531.069, 3518.652, 3505.741,
      ],
      [
        1314.791, 1364.297, 1423.082, 1510.956, 1680.711, 1912.803, 2140.943,
        2355.412, 2554.975, 2738.556, 2905.016, 3053.372, 3182.909, 3293.227,
        3384.37, 3457.065, 3512.864, 3554.021, 3583.097, 3602.564, 3614.55,
        3620.766, 3622.537, 3620.864, 3616.505, 3610.029, 3601.871, 3592.361,
        3581.756, 3570.257, 3558.02, 3545.17, 3531.809,
      ],
      [
        1333.617, 1384.125, 1443.077, 1527.838, 1686.091, 1913.418, 2141.18,
        2355.912, 2556.151, 2740.92, 2909.15, 3059.85, 3192.224, 3305.727,
        3400.195, 3476.099, 3534.751, 3578.244, 3609.1, 3629.834, 3642.664,
        3649.394, 3651.425, 3649.824, 3645.395, 3638.743, 3630.329, 3620.502,
        3609.534, 3597.635, 3584.97, 3571.67, 3557.842,
      ],
      [
        1352.801, 1404.379, 1463.614, 1545.778, 1693.13, 1914.316, 2141.443,
        2356.373, 2557.223, 2743.085, 2912.967, 3065.892, 3201.002, 3317.628,
        3415.418, 3494.591, 3556.201, 3602.154, 3634.903, 3656.998, 3670.739,
        3678.025, 3680.343, 3678.827, 3674.332, 3667.502, 3658.824, 3648.669,
        3637.324, 3625.01, 3611.9, 3598.135, 3583.822,
      ],
      [
        1372.338, 1425.059, 1484.687, 1564.708, 1702.022, 1915.629, 2141.755,
        2356.805, 2558.2, 2745.065, 2916.486, 3071.513, 3209.25, 3328.928,
        3430.022, 3512.509, 3577.174, 3625.71, 3660.473, 3684.026, 3698.749,
        3706.64, 3709.273, 3707.857, 3703.301, 3696.289, 3687.339, 3676.845,
        3665.108, 3652.363, 3638.793, 3624.543, 3609.729,
      ],
      [
        1392.221, 1446.16, 1506.293, 1584.572, 1712.876, 1917.538, 2142.151,
        2357.219, 2559.092, 2746.873, 2919.724, 3076.73, 3216.981, 3339.625,
        3443.989, 3529.822, 3597.632, 3648.873, 3685.773, 3710.888, 3726.671,
        3735.218, 3738.198, 3736.897, 3732.284, 3725.087, 3715.857, 3705.01,
        3692.867, 3679.675, 3665.626, 3650.874, 3635.54,
      ],
      [
        1412.44, 1467.676, 1528.424, 1605.318, 1725.709, 1920.28, 2142.677,
        2357.627, 2559.909, 2748.525, 2922.698, 3081.562, 3224.207, 3349.723,
        3457.309, 3546.503, 3617.537, 3671.602, 3710.765, 3737.552, 3754.478,
        3763.736, 3767.098, 3765.929, 3761.263, 3753.878, 3744.359, 3733.148,
        3720.583, 3706.926, 3692.38, 3677.107, 3661.233,
      ],
    ],
    mw: [
      [
        0.01168582, 0.01215876, 0.01212869, 0.01270108, 0.01336837, 0.01403411,
        0.01469157, 0.01533056, 0.01594103, 0.01651753, 0.01705911, 0.01756687,
        0.01804252, 0.01848808, 0.01890599, 0.01929898, 0.01966978, 0.02002089,
        0.02035447, 0.02067236, 0.02097609, 0.02126692, 0.02154594, 0.02181405,
        0.02207203, 0.02232055, 0.02256021, 0.02279154, 0.02301499, 0.023231,
        0.02343994, 0.02364216, 0.02383798,
      ],
      [
        0.01176338, 0.01220573, 0.01215497, 0.0127015, 0.01336842, 0.01403441,
        0.01469288, 0.01533431, 0.0159488, 0.01653044, 0.01707762, 0.01759098,
        0.01807192, 0.01852225, 0.01894431, 0.0193408, 0.01971449, 0.02006794,
        0.02040343, 0.02072286, 0.02102782, 0.02131964, 0.02159944, 0.02186816,
        0.0221266, 0.02237548, 0.02261538, 0.02284687, 0.0230704, 0.02328642,
        0.02349531, 0.02369743, 0.0238931,
      ],
      [
        0.01184562, 0.01225438, 0.01218502, 0.01270214, 0.01336847, 0.01403468,
        0.01469408, 0.01533777, 0.01595611, 0.01654279, 0.01709557, 0.01761463,
        0.01810099, 0.01855627, 0.01898266, 0.0193828, 0.01975951, 0.02011543,
        0.02045291, 0.02077393, 0.02108017, 0.02137301, 0.02165361, 0.02192295,
        0.02218188, 0.0224311, 0.02267124, 0.02290288, 0.02312648, 0.0233425,
        0.02355132, 0.02375332, 0.02394882,
      ],
      [
        0.01193267, 0.01230477, 0.01221868, 0.0127031, 0.01336853, 0.01403492,
        0.01469516, 0.01534096, 0.01596297, 0.01655457, 0.01711294, 0.01763776,
        0.01812969, 0.01859009, 0.01902098, 0.01942495, 0.01980483, 0.02016333,
        0.02050289, 0.02082558, 0.02113315, 0.02142704, 0.02170846, 0.02197844,
        0.02223784, 0.02248741, 0.0227278, 0.02295957, 0.02318322, 0.02339922,
        0.02360797, 0.02380983, 0.02400513,
      ],
      [
        0.01202471, 0.01235697, 0.01225582, 0.01270454, 0.0133686, 0.01403515,
        0.01469616, 0.01534391, 0.0159694, 0.01656577, 0.01712971, 0.01766033,
        0.01815797, 0.01862366, 0.01905924, 0.01946722, 0.01985042, 0.02021162,
        0.02055336, 0.02087779, 0.02118674, 0.02148171, 0.02176399, 0.02203462,
        0.0222945, 0.02254442, 0.02278503, 0.02301693, 0.02324064, 0.0234566,
        0.02366525, 0.02386695, 0.02406204,
      ],
      [
        0.0121219, 0.01241105, 0.01229628, 0.0127067, 0.0133687, 0.01403535,
        0.01469706, 0.01534662, 0.0159754, 0.01657641, 0.01714584, 0.01768233,
        0.01818577, 0.01865693, 0.0190974, 0.01950956, 0.01989624, 0.02026028,
        0.0206043, 0.02093055, 0.02124093, 0.02153703, 0.02182018, 0.02209147,
        0.02235185, 0.02260212, 0.02284296, 0.02307498, 0.02329871, 0.02351462,
        0.02372315, 0.02392467, 0.02411953,
      ],
      [
        0.01222438, 0.0124671, 0.01233995, 0.01270989, 0.01336882, 0.01403554,
        0.01469788, 0.01534911, 0.01598099, 0.01658648, 0.01716133, 0.01770369,
        0.01821306, 0.01868984, 0.01913539, 0.01955193, 0.01994227, 0.02030929,
        0.0206557, 0.02098384, 0.02129572, 0.02159299, 0.02187704, 0.02214902,
        0.02240989, 0.0226605, 0.02290156, 0.02313369, 0.02335744, 0.02357329,
        0.02378168, 0.02398299, 0.0241776,
      ],
      [
        0.01233231, 0.0125252, 0.01238673, 0.01271458, 0.013369, 0.01403572,
        0.01469862, 0.01535139, 0.01598619, 0.01659599, 0.01717616, 0.01772441,
        0.01823979, 0.01872235, 0.01917317, 0.01959429, 0.01998846, 0.02035861,
        0.02070753, 0.02103766, 0.0213511, 0.02164959, 0.02193456, 0.02220723,
        0.02246862, 0.02271957, 0.02296084, 0.02319307, 0.02341682, 0.02363258,
        0.02384081, 0.0240419, 0.02423622,
      ],
      [
        0.01244584, 0.01258544, 0.01243655, 0.01272136, 0.01336925, 0.01403589,
        0.0146993, 0.01535348, 0.01599102, 0.01660493, 0.01719033, 0.01774443,
        0.0182659, 0.0187544, 0.01921068, 0.01963659, 0.02003478, 0.02040822,
        0.02075977, 0.02109198, 0.02140705, 0.0217068, 0.02199273, 0.02226612,
        0.02252802, 0.02277932, 0.0230208, 0.02325311, 0.02347684, 0.0236925,
        0.02390055, 0.02410139, 0.0242954,
      ],
      [
        0.01256512, 0.01264792, 0.01248939, 0.01273098, 0.01336963, 0.01403605,
        0.01469992, 0.01535539, 0.01599548, 0.01661334, 0.01720382, 0.01776375,
        0.01829136, 0.01878593, 0.01924787, 0.01967877, 0.02008119, 0.02045808,
        0.02081239, 0.0211468, 0.02146357, 0.02176462, 0.02205155, 0.02232567,
        0.02258809, 0.02283974, 0.02308141, 0.0233138, 0.0235375, 0.02375303,
        0.02396087, 0.02416144, 0.02435511,
      ],
      [
        0.0126903, 0.01271274, 0.01254523, 0.01274429, 0.01337018, 0.01403622,
        0.01470048, 0.01535713, 0.01599961, 0.01662121, 0.01721663, 0.01778233,
        0.01831612, 0.01881689, 0.01928467, 0.01972078, 0.02012762, 0.02050815,
        0.02086538, 0.02120207, 0.02152063, 0.02182305, 0.02211101, 0.02238588,
        0.02264883, 0.02290083, 0.02314269, 0.02337514, 0.02359878, 0.02381417,
        0.02402178, 0.02422204, 0.02441535,
      ],
      [
        0.01282149, 0.01278, 0.01260409, 0.01276218, 0.01337101, 0.0140364,
        0.014701, 0.01535873, 0.01600342, 0.01662857, 0.01722877, 0.01780015,
        0.01834014, 0.01884722, 0.01932102, 0.01976256, 0.02017404, 0.0205584,
        0.02091869, 0.02125779, 0.02157821, 0.02188206, 0.02217109, 0.02244674,
        0.02271023, 0.02296257, 0.02320461, 0.0234371, 0.02366068, 0.0238759,
        0.02408325, 0.02428318, 0.02447609,
      ],
      [
        0.01295884, 0.01284982, 0.012666, 0.01278544, 0.01337225, 0.0140366,
        0.01470147, 0.01536019, 0.01600692, 0.01663543, 0.01724024, 0.0178172,
        0.01836338, 0.01887686, 0.01935686, 0.01980404, 0.02022039, 0.02060877,
        0.02097229, 0.02131393, 0.02163631, 0.02194165, 0.02223178, 0.02250823,
        0.02277227, 0.02302496, 0.02326718, 0.0234997, 0.02372318, 0.0239382,
        0.02414527, 0.02434484, 0.02453732,
      ],
      [
        0.01310246, 0.01292231, 0.01273101, 0.01281469, 0.01337411, 0.01403685,
        0.01470191, 0.01536152, 0.01601015, 0.01664182, 0.01725104, 0.01783347,
        0.01838582, 0.01890577, 0.01939213, 0.01984516, 0.02026661, 0.02065922,
        0.02102614, 0.02137045, 0.02169489, 0.02200179, 0.02229307, 0.02257035,
        0.02283495, 0.02308798, 0.02333036, 0.0235629, 0.02378627, 0.02400107,
        0.02420783, 0.024407, 0.02459901,
      ],
      [
        0.01325245, 0.01299761, 0.01279919, 0.01285029, 0.01337689, 0.01403718,
        0.01470233, 0.01536273, 0.01601311, 0.01664775, 0.0172612, 0.01784895,
        0.01840741, 0.01893389, 0.01942675, 0.01988585, 0.02031263, 0.0207097,
        0.02108021, 0.02142734, 0.02175394, 0.02206246, 0.02235495, 0.02263308,
        0.02289825, 0.02315162, 0.02339416, 0.0236267, 0.02384993, 0.02406449,
        0.0242709, 0.02446964, 0.02466115,
      ],
      [
        0.01339013, 0.01307583, 0.01287064, 0.01289238, 0.01338101, 0.01403762,
        0.01470273, 0.01536384, 0.01601583, 0.01665324, 0.01727073, 0.01786364,
        0.01842814, 0.01896117, 0.01946067, 0.01992605, 0.0203584, 0.02076015,
        0.02113444, 0.02148454, 0.02181342, 0.02212365, 0.02241739, 0.02269641,
        0.02296217, 0.02321588, 0.02345856, 0.02369107, 0.02391415, 0.02412843,
        0.02433446, 0.02453274, 0.0247237,
      ],
      [
        0.01347482, 0.01315712, 0.01294544, 0.0129409, 0.01338707, 0.01403824,
        0.01470313, 0.01536486, 0.01601832, 0.01665832, 0.01727964, 0.01787755,
        0.01844799, 0.01898757, 0.01949381, 0.01996567, 0.02040383, 0.02081051,
        0.0211888, 0.02154203, 0.02187331, 0.02218534, 0.02248038, 0.02276031,
        0.02302667, 0.02328073, 0.02352354, 0.02375602, 0.02397891, 0.02419287,
        0.02439849, 0.02459627, 0.02478665,
      ],
      [
        0.01356269, 0.01324161, 0.01302372, 0.01299573, 0.01339589, 0.01403914,
        0.01470354, 0.01536581, 0.01602061, 0.01666302, 0.01728795, 0.01789067,
        0.01846693, 0.01901305, 0.01952613, 0.02000465, 0.02044886, 0.02086071,
        0.02124322, 0.02159976, 0.02193357, 0.02224749, 0.0225439, 0.02282478,
        0.02309175, 0.02334615, 0.02358909, 0.0238215, 0.02404418, 0.0242578,
        0.02446297, 0.02466021, 0.02484996,
      ],
      [
        0.01365387, 0.01332947, 0.0131056, 0.01305667, 0.01340848, 0.01404046,
        0.014704, 0.01536668, 0.01602271, 0.01666734, 0.0172957, 0.01790303,
        0.01848497, 0.01903757, 0.01955755, 0.02004291, 0.02049342, 0.0209107,
        0.02129766, 0.0216577, 0.02199418, 0.02231008, 0.02260793, 0.02288979,
        0.02315739, 0.02341214, 0.02365518, 0.02388751, 0.02410994, 0.02432319,
        0.02452787, 0.02472452, 0.02491361,
      ],
      [
        0.01374849, 0.01342083, 0.01319122, 0.01312354, 0.01342605, 0.01404241,
        0.01470454, 0.01536751, 0.01602464, 0.01667134, 0.0173029, 0.01791464,
        0.0185021, 0.01906111, 0.01958803, 0.02008037, 0.02053741, 0.02096039,
        0.02135204, 0.02171579, 0.02205509, 0.02237309, 0.02267243, 0.02295533,
        0.02322357, 0.02347866, 0.0237218, 0.02395402, 0.02417618, 0.02438902,
        0.02459317, 0.02478919, 0.02497756,
      ],
      [
        0.0138467, 0.01351586, 0.01328072, 0.01319621, 0.0134499, 0.0140453,
        0.01470521, 0.01536831, 0.01602641, 0.01667501, 0.01730958, 0.01792552,
        0.01851833, 0.01908364, 0.01961751, 0.02011697, 0.02058077, 0.02100971,
        0.02140631, 0.02177398, 0.02211627, 0.02243648, 0.02273739, 0.02302136,
        0.02329027, 0.02354569, 0.02378892, 0.02402101, 0.02424286, 0.02445525,
        0.02465882, 0.02485416, 0.02504177,
      ],
      [
        0.01394863, 0.01361471, 0.01337427, 0.01327457, 0.01348128, 0.01404959,
        0.0147061, 0.01536911, 0.01602806, 0.0166784, 0.01731577, 0.0179357,
        0.01853365, 0.01910515, 0.01964595, 0.02015263, 0.0206234, 0.02105858,
        0.0214604, 0.02183221, 0.02217768, 0.02250021, 0.02280278, 0.02308786,
        0.02335746, 0.02361323, 0.02385652, 0.02408845, 0.02430996, 0.02452185,
        0.02472481, 0.02491942, 0.02510622,
      ],
      [
        0.01405442, 0.01371757, 0.01347202, 0.01335855, 0.01352119, 0.01405593,
        0.01470732, 0.01536993, 0.01602959, 0.01668152, 0.01732151, 0.0179452,
        0.01854809, 0.01912562, 0.01967331, 0.0201873, 0.02066523, 0.02110692,
        0.02151424, 0.02189044, 0.02223925, 0.02256426, 0.02286857, 0.02315481,
        0.02342511, 0.02368122, 0.02392456, 0.02415631, 0.02437744, 0.0245888,
        0.02479109, 0.02498492, 0.02517085,
      ],
      [
        0.01416423, 0.01382459, 0.01357417, 0.01344816, 0.01357033, 0.01406519,
        0.01470904, 0.01537083, 0.01603104, 0.0166844, 0.01732681, 0.01795405,
        0.01856167, 0.01914506, 0.01969955, 0.02022089, 0.02070618, 0.02115464,
        0.02156774, 0.02194859, 0.02230096, 0.02262857, 0.02293472, 0.02322218,
        0.02349321, 0.02374966, 0.02399303, 0.02422456, 0.02444528, 0.02465606,
        0.02485762, 0.02505063, 0.02523562,
      ],
      [
        0.01427817, 0.01393594, 0.01368087, 0.01354341, 0.01362903, 0.01407857,
        0.01471154, 0.01537187, 0.01603244, 0.01668708, 0.01733172, 0.01796228,
        0.01857441, 0.01916347, 0.01972465, 0.02025337, 0.02074617, 0.02120165,
        0.02162083, 0.0220066, 0.02236274, 0.02269312, 0.02300121, 0.02328994,
        0.02356172, 0.02381851, 0.0240619, 0.02429318, 0.02451344, 0.02472358,
        0.02492438, 0.02511649, 0.0253005,
      ],
    ],
    gammaS: [
      [
        1.144221, 1.177516, 1.253197, 1.29978, 1.284333, 1.266855, 1.245963,
        1.222015, 1.198291, 1.17768, 1.160983, 1.147859, 1.137786, 1.130284,
        1.12486, 1.121019, 1.118319, 1.116412, 1.115051, 1.114065, 1.113341,
        1.112804, 1.112402, 1.112102, 1.11188, 1.11172, 1.11161, 1.111543,
        1.111512, 1.111512, 1.111541, 1.111596, 1.111676,
      ],
      [
        1.145496, 1.178283, 1.245662, 1.299585, 1.284417, 1.267279, 1.247109,
        1.223994, 1.200775, 1.180254, 1.163402, 1.150026, 1.139679, 1.13192,
        1.126284, 1.12228, 1.119463, 1.117475, 1.116058, 1.115035, 1.114285,
        1.11373, 1.113316, 1.113008, 1.112781, 1.112618, 1.112507, 1.11244,
        1.11241, 1.112413, 1.112445, 1.112503, 1.112587,
      ],
      [
        1.146771, 1.179104, 1.23904, 1.299289, 1.284491, 1.267664, 1.248165,
        1.225864, 1.203185, 1.182806, 1.165836, 1.152227, 1.141611, 1.133594,
        1.127737, 1.123564, 1.120623, 1.11855, 1.117074, 1.116011, 1.115234,
        1.114661, 1.114234, 1.113918, 1.113685, 1.113519, 1.113407, 1.11334,
        1.113312, 1.113317, 1.113352, 1.113415, 1.113503,
      ],
      [
        1.148042, 1.179972, 1.23338, 1.298842, 1.284556, 1.268012, 1.249136,
        1.227625, 1.205514, 1.185326, 1.168278, 1.154458, 1.143581, 1.135304,
        1.129222, 1.124871, 1.121801, 1.119637, 1.118099, 1.116993, 1.116188,
        1.115595, 1.115156, 1.11483, 1.114593, 1.114424, 1.11431, 1.114244,
        1.114217, 1.114224, 1.114263, 1.11433, 1.114424,
      ],
      [
        1.149307, 1.180881, 1.228642, 1.298175, 1.28461, 1.268328, 1.250027,
        1.229276, 1.207755, 1.187807, 1.170722, 1.156715, 1.145588, 1.137052,
        1.130738, 1.126202, 1.122995, 1.120735, 1.119132, 1.117982, 1.117146,
        1.116533, 1.116079, 1.115745, 1.115502, 1.11533, 1.115216, 1.11515,
        1.115124, 1.115135, 1.115178, 1.115249, 1.115348,
      ],
      [
        1.150561, 1.181826, 1.224739, 1.297194, 1.284653, 1.268613, 1.250843,
        1.230821, 1.209905, 1.19024, 1.173158, 1.158992, 1.147629, 1.138836,
        1.132285, 1.127557, 1.124207, 1.121846, 1.120173, 1.118976, 1.118108,
        1.117473, 1.117006, 1.116662, 1.116413, 1.116238, 1.116123, 1.116058,
        1.116034, 1.116048, 1.116095, 1.116171, 1.116276,
      ],
      [
        1.151801, 1.1828, 1.221568, 1.295777, 1.284684, 1.268871, 1.25159,
        1.232262, 1.211959, 1.192617, 1.175581, 1.161285, 1.149702, 1.140656,
        1.133865, 1.128938, 1.125437, 1.122968, 1.121222, 1.119974, 1.119073,
        1.118416, 1.117933, 1.11758, 1.117326, 1.117148, 1.117032, 1.116967,
        1.116946, 1.116963, 1.117014, 1.117096, 1.117207,
      ],
      [
        1.153023, 1.183797, 1.219024, 1.293776, 1.284698, 1.269104, 1.252273,
        1.233603, 1.213914, 1.194931, 1.177982, 1.163588, 1.151802, 1.14251,
        1.135477, 1.130343, 1.126685, 1.124103, 1.122278, 1.120978, 1.120041,
        1.11936, 1.118862, 1.118499, 1.118238, 1.118057, 1.117941, 1.117877,
        1.117858, 1.117879, 1.117935, 1.118022, 1.11814,
      ],
      [
        1.154221, 1.184812, 1.21701, 1.291044, 1.284691, 1.269314, 1.252895,
        1.234846, 1.215767, 1.197173, 1.180352, 1.165894, 1.153927, 1.144397,
        1.137121, 1.131775, 1.127951, 1.12525, 1.123342, 1.121985, 1.121011,
        1.120305, 1.119791, 1.119417, 1.119151, 1.118967, 1.118849, 1.118787,
        1.118771, 1.118796, 1.118857, 1.11895, 1.119075,
      ],
      [
        1.155392, 1.185839, 1.215442, 1.287465, 1.284654, 1.269502, 1.253462,
        1.235997, 1.217519, 1.199339, 1.182685, 1.168196, 1.156071, 1.146314,
        1.138796, 1.133233, 1.129236, 1.126409, 1.124413, 1.122997, 1.121983,
        1.12125, 1.120719, 1.120335, 1.120062, 1.119875, 1.119757, 1.119696,
        1.119683, 1.119712, 1.119779, 1.119879, 1.120011,
      ],
      [
        1.15653, 1.186871, 1.214245, 1.283015, 1.284576, 1.26967, 1.253978,
        1.23706, 1.219169, 1.201422, 1.184971, 1.170487, 1.15823, 1.14826,
        1.140502, 1.134716, 1.13054, 1.12758, 1.125491, 1.124012, 1.122955,
        1.122195, 1.121646, 1.121251, 1.120972, 1.120782, 1.120664, 1.120604,
        1.120595, 1.120629, 1.120701, 1.120808, 1.120948,
      ],
      [
        1.157629, 1.187902, 1.213358, 1.277809, 1.284438, 1.269819, 1.254447,
        1.238039, 1.220718, 1.203418, 1.187203, 1.172759, 1.160397, 1.150231,
        1.142238, 1.136227, 1.131863, 1.128763, 1.126576, 1.125029, 1.123929,
        1.123139, 1.122571, 1.122165, 1.121879, 1.121686, 1.121568, 1.121511,
        1.121505, 1.121544, 1.121622, 1.121737, 1.121884,
      ],
      [
        1.158684, 1.188926, 1.212725, 1.272101, 1.284213, 1.269949, 1.254872,
        1.238939, 1.222168, 1.205324, 1.189375, 1.175005, 1.162567, 1.152223,
        1.144002, 1.137763, 1.133206, 1.129958, 1.127667, 1.12605, 1.124901,
        1.124081, 1.123494, 1.123075, 1.122782, 1.122587, 1.122469, 1.122414,
        1.122412, 1.122456, 1.122542, 1.122664, 1.12282,
      ],
      [
        1.159688, 1.189936, 1.212301, 1.266232, 1.283863, 1.27006, 1.255257,
        1.239765, 1.22352, 1.207137, 1.191479, 1.177217, 1.164734, 1.154233,
        1.145792, 1.139325, 1.134569, 1.131166, 1.128764, 1.127072, 1.125873,
        1.12502, 1.124412, 1.123981, 1.123682, 1.123484, 1.123366, 1.123313,
        1.123316, 1.123366, 1.123459, 1.123589, 1.123755,
      ],
      [
        1.160635, 1.190923, 1.212046, 1.26054, 1.283332, 1.270151, 1.255605,
        1.240521, 1.224778, 1.208855, 1.19351, 1.179388, 1.166889, 1.156254,
        1.147607, 1.140912, 1.135951, 1.132387, 1.129868, 1.128095, 1.126844,
        1.125956, 1.125326, 1.124882, 1.124576, 1.124375, 1.124258, 1.124208,
        1.124216, 1.124272, 1.124373, 1.124512, 1.124688,
      ],
      [
        1.178324, 1.191881, 1.211926, 1.255286, 1.282549, 1.270219, 1.255919,
        1.241212, 1.225945, 1.210476, 1.195463, 1.181509, 1.169026, 1.158282,
        1.149442, 1.142523, 1.137352, 1.13362, 1.130977, 1.129119, 1.127812,
        1.126888, 1.126235, 1.125777, 1.125464, 1.125261, 1.125144, 1.125098,
        1.125111, 1.125174, 1.125282, 1.125431, 1.125617,
      ],
      [
        1.179328, 1.192801, 1.21191, 1.250618, 1.281425, 1.270258, 1.256202,
        1.241843, 1.227025, 1.212003, 1.197333, 1.183574, 1.171137, 1.160311,
        1.151295, 1.144156, 1.138773, 1.134865, 1.132092, 1.130144, 1.128776,
        1.127814, 1.127137, 1.126666, 1.126345, 1.126139, 1.126024, 1.125981,
        1.126, 1.126071, 1.126188, 1.126346, 1.126543,
      ],
      [
        1.180267, 1.193674, 1.211969, 1.246587, 1.27986, 1.270262, 1.256454,
        1.242417, 1.228022, 1.213434, 1.199117, 1.185577, 1.173215, 1.162333,
        1.153161, 1.145811, 1.140213, 1.136123, 1.133213, 1.131169, 1.129737,
        1.128734, 1.128032, 1.127546, 1.127218, 1.12701, 1.126896, 1.126857,
        1.126882, 1.12696, 1.127087, 1.127256, 1.127465,
      ],
      [
        1.181134, 1.194491, 1.21208, 1.24317, 1.277769, 1.270217, 1.256678,
        1.242939, 1.228939, 1.214772, 1.200812, 1.187511, 1.175252, 1.164344,
        1.155036, 1.147484, 1.141671, 1.137393, 1.134339, 1.132193, 1.130694,
        1.129647, 1.128919, 1.128417, 1.128081, 1.127871, 1.127759, 1.127725,
        1.127756, 1.127843, 1.127979, 1.12816, 1.12838,
      ],
      [
        1.18192, 1.195242, 1.212218, 1.240306, 1.275107, 1.270105, 1.256873,
        1.243412, 1.229782, 1.216018, 1.202417, 1.189371, 1.177242, 1.166334,
        1.156914, 1.149173, 1.143147, 1.138675, 1.13547, 1.133216, 1.131645,
        1.130552, 1.129796, 1.129278, 1.128935, 1.128722, 1.128612, 1.128583,
        1.128621, 1.128718, 1.128864, 1.129056, 1.12929,
      ],
      [
        1.182616, 1.195916, 1.212362, 1.237915, 1.271906, 1.269899, 1.257039,
        1.243839, 1.230554, 1.217176, 1.20393, 1.191153, 1.179177, 1.168298,
        1.158789, 1.150875, 1.144638, 1.139969, 1.136605, 1.134238, 1.13259,
        1.131449, 1.130662, 1.130128, 1.129776, 1.129562, 1.129454, 1.12943,
        1.129477, 1.129583, 1.12974, 1.129945, 1.130192,
      ],
      [
        1.183213, 1.196503, 1.212489, 1.235913, 1.268287, 1.269564, 1.257175,
        1.244223, 1.231259, 1.218248, 1.205352, 1.192854, 1.181051, 1.170227,
        1.160656, 1.152585, 1.146144, 1.141274, 1.137745, 1.135258, 1.133528,
        1.132335, 1.131517, 1.130965, 1.130605, 1.130389, 1.130284, 1.130266,
        1.130321, 1.130437, 1.130607, 1.130824, 1.131085,
      ],
      [
        1.1837, 1.196989, 1.21258, 1.234218, 1.264443, 1.269048, 1.257276,
        1.244567, 1.231901, 1.219237, 1.206684, 1.194469, 1.182858, 1.172114,
        1.162507, 1.154299, 1.147662, 1.14259, 1.13889, 1.136275, 1.134459,
        1.13321, 1.132359, 1.131788, 1.13142, 1.131203, 1.1311, 1.131089,
        1.131153, 1.13128, 1.131462, 1.131693, 1.131969,
      ],
      [
        1.184067, 1.197365, 1.212615, 1.232756, 1.260581, 1.268293, 1.257334,
        1.244872, 1.232483, 1.220147, 1.207925, 1.195998, 1.184594, 1.173953,
        1.164336, 1.156012, 1.149191, 1.143915, 1.140038, 1.137288, 1.135381,
        1.134073, 1.133186, 1.132596, 1.13222, 1.132001, 1.131902, 1.131898,
        1.131971, 1.13211, 1.132305, 1.132551, 1.132842,
      ],
      [
        1.184304, 1.197615, 1.212572, 1.231462, 1.256872, 1.267232, 1.25734,
        1.245138, 1.233009, 1.220981, 1.209079, 1.197439, 1.186253, 1.175737,
        1.166135, 1.157719, 1.150725, 1.145249, 1.141189, 1.138299, 1.136293,
        1.134923, 1.133999, 1.133389, 1.133003, 1.132783, 1.132688, 1.132691,
        1.132775, 1.132926, 1.133135, 1.133395, 1.133703,
      ],
    ],
    muPaS: [
      [
        3.844574e-5, 3.967358e-5, 4.270532e-5, 4.956506e-5, 5.654282e-5,
        6.30711e-5, 6.911717e-5, 7.458776e-5, 7.941056e-5, 8.358676e-5,
        8.716788e-5, 9.021422e-5, 9.277912e-5, 9.491316e-5, 9.666971e-5,
        9.810427e-5, 9.927006e-5, 0.0001002143, 0.0001009769, 0.0001015901,
        0.0001020803, 0.0001024683, 0.000102771, 0.0001030019, 0.000103172,
        0.0001032905, 0.0001033646, 0.0001034006, 0.0001034036, 0.000103378,
        0.0001033273, 0.0001032548, 0.0001031631,
      ],
      [
        3.87319e-5, 3.999096e-5, 4.288748e-5, 4.956771e-5, 5.654365e-5,
        6.307676e-5, 6.914016e-5, 7.46491e-5, 7.953095e-5, 8.377825e-5,
        8.743375e-5, 9.055241e-5, 9.318446e-5, 9.537817e-5, 9.71854e-5,
        9.866119e-5, 9.985932e-5, 0.0001008281, 0.0001016087, 0.0001022347,
        0.0001027333, 0.0001031264, 0.0001034314, 0.0001036625, 0.0001038311,
        0.0001039466, 0.0001040167, 0.0001040477, 0.0001040451, 0.0001040132,
        0.0001039559, 0.0001038764, 0.0001037773,
      ],
      [
        3.902268e-5, 4.031507e-5, 4.309411e-5, 4.957167e-5, 5.654445e-5,
        6.308189e-5, 6.916111e-5, 7.470577e-5, 7.964405e-5, 8.396098e-5,
        8.769075e-5, 9.088267e-5, 9.358353e-5, 9.583899e-5, 9.769906e-5,
        9.921811e-5, 0.0001004503, 0.000101445, 0.0001022446, 0.000102884,
        0.0001033916, 0.00010379, 0.0001040975, 0.0001043288, 0.0001044958,
        0.0001046082, 0.0001046741, 0.0001047, 0.0001046915, 0.0001046532,
        0.0001045889, 0.0001045021, 0.0001043954,
      ],
      [
        3.931811e-5, 4.064604e-5, 4.33237e-5, 4.957763e-5, 5.654524e-5,
        6.308652e-5, 6.918018e-5, 7.475804e-5, 7.975004e-5, 8.41349e-5,
        8.793858e-5, 9.120453e-5, 9.397576e-5, 9.629502e-5, 9.821017e-5,
        9.977458e-5, 0.0001010426, 0.0001020646, 0.0001028844, 0.000103538,
        0.000104055, 0.000104459, 0.0001047692, 0.0001050007, 0.000105166,
        0.0001052753, 0.0001053368, 0.0001053574, 0.0001053427, 0.0001052977,
        0.0001052262, 0.0001051318, 0.0001050172,
      ],
      [
        3.961825e-5, 4.098398e-5, 4.357459e-5, 4.958655e-5, 5.654607e-5,
        6.309072e-5, 6.919755e-5, 7.480614e-5, 7.984913e-5, 8.430002e-5,
        8.8177e-5, 9.151753e-5, 9.436058e-5, 9.674567e-5, 9.871817e-5,
        0.0001003301, 0.0001016359, 0.0001026868, 0.0001035278, 0.0001041964,
        0.0001047234, 0.0001051335, 0.0001054464, 0.0001056782, 0.0001058418,
        0.0001059478, 0.0001060047, 0.0001060197, 0.0001059987, 0.0001059467,
        0.0001058677, 0.0001057654, 0.0001056426,
      ],
      [
        3.992311e-5, 4.132901e-5, 4.38452e-5, 4.959987e-5, 5.654695e-5,
        6.309451e-5, 6.921331e-5, 7.485036e-5, 7.994154e-5, 8.445637e-5,
        8.840579e-5, 9.182125e-5, 9.473741e-5, 9.71903e-5, 9.922244e-5,
        0.0001008842, 0.0001022298, 0.0001033111, 0.0001041746, 0.0001048592,
        0.0001053968, 0.0001058131, 0.0001061291, 0.0001063612, 0.000106523,
        0.0001066256, 0.0001066777, 0.000106687, 0.0001066593, 0.0001066,
        0.0001065132, 0.0001064027, 0.0001062714,
      ],
      [
        4.023272e-5, 4.168125e-5, 4.413406e-5, 4.96196e-5, 5.654797e-5,
        6.309795e-5, 6.922763e-5, 7.489092e-5, 8.002755e-5, 8.460405e-5,
        8.86248e-5, 9.21153e-5, 9.51057e-5, 9.762828e-5, 9.972237e-5,
        0.0001014363, 0.0001028237, 0.0001039373, 0.0001048246, 0.000105526,
        0.0001060748, 0.000106498, 0.0001068171, 0.0001070496, 0.0001072096,
        0.0001073086, 0.0001073558, 0.000107359, 0.0001073245, 0.0001072576,
        0.0001071627, 0.0001070436, 0.0001069034,
      ],
      [
        4.054709e-5, 4.204086e-5, 4.443993e-5, 4.964853e-5, 5.654923e-5,
        6.310108e-5, 6.924062e-5, 7.492809e-5, 8.01074e-5, 8.474318e-5,
        8.883392e-5, 9.23993e-5, 9.54649e-5, 9.805896e-5, 0.0001002173,
        0.0001019859, 0.0001034173, 0.000104565, 0.0001054775, 0.0001061968,
        0.0001067575, 0.0001071878, 0.0001075103, 0.0001077433, 0.0001079015,
        0.0001079967, 0.0001080388, 0.0001080357, 0.000107994, 0.0001079192,
        0.0001078159, 0.0001076879, 0.0001075386,
      ],
      [
        4.086624e-5, 4.240792e-5, 4.476175e-5, 4.969032e-5, 5.655089e-5,
        6.310397e-5, 6.925241e-5, 7.496209e-5, 8.018141e-5, 8.487391e-5,
        8.903307e-5, 9.267295e-5, 9.581446e-5, 9.848168e-5, 0.0001007065,
        0.0001025322, 0.0001040101, 0.0001051937, 0.000106133, 0.0001068712,
        0.0001074446, 0.0001078825, 0.0001082087, 0.0001084422, 0.0001085985,
        0.0001086899, 0.0001087266, 0.0001087169, 0.0001086678, 0.0001085848,
        0.0001084727, 0.0001083355, 0.0001081766,
      ],
      [
        4.119012e-5, 4.278258e-5, 4.509864e-5, 4.974946e-5, 5.655316e-5,
        6.310662e-5, 6.92631e-5, 7.499316e-5, 8.024984e-5, 8.499647e-5,
        8.922223e-5, 9.293597e-5, 9.615389e-5, 9.889578e-5, 0.0001011894,
        0.0001030747, 0.0001046014, 0.0001058231, 0.0001067907, 0.0001075491,
        0.0001081359, 0.000108582, 0.0001089121, 0.0001091462, 0.0001093005,
        0.0001093879, 0.000109419, 0.0001094026, 0.0001093456, 0.0001092541,
        0.0001091329, 0.0001089861, 0.0001088173,
      ],
      [
        4.151875e-5, 4.316496e-5, 4.544985e-5, 4.983109e-5, 5.65564e-5,
        6.310909e-5, 6.927279e-5, 7.502152e-5, 8.031299e-5, 8.511107e-5,
        8.940145e-5, 9.318811e-5, 9.648272e-5, 9.930062e-5, 0.0001016652,
        0.0001036126, 0.0001051906, 0.0001064526, 0.0001074503, 0.0001082301,
        0.0001088313, 0.000109286, 0.0001096203, 0.0001098552, 0.0001100074,
        0.0001100907, 0.000110116, 0.0001100924, 0.0001100274, 0.0001099269,
        0.0001097963, 0.0001096396, 0.0001094604,
      ],
      [
        4.18521e-5, 4.355517e-5, 4.581483e-5, 4.994037e-5, 5.656113e-5,
        6.311146e-5, 6.928157e-5, 7.504737e-5, 8.037116e-5, 8.521798e-5,
        8.95708e-5, 9.342922e-5, 9.680049e-5, 9.969555e-5, 0.0001021331,
        0.0001041452, 0.0001057773, 0.0001070817, 0.0001081114, 0.000108914,
        0.0001095304, 0.0001099944, 0.0001103332, 0.0001105689, 0.0001107191,
        0.0001107981, 0.0001108173, 0.0001107864, 0.0001107129, 0.0001106032,
        0.0001104626, 0.0001102956, 0.0001101058,
      ],
      [
        4.219015e-5, 4.395335e-5, 4.619309e-5, 5.008182e-5, 5.656808e-5,
        6.311383e-5, 6.928955e-5, 7.507092e-5, 8.042466e-5, 8.531749e-5,
        8.973041e-5, 9.365917e-5, 9.710684e-5, 0.00010008, 0.0001025924,
        0.0001046719, 0.0001063606, 0.00010771, 0.0001087735, 0.0001096004,
        0.0001102331, 0.000110707, 0.0001110507, 0.0001112873, 0.0001114354,
        0.0001115099, 0.0001115229, 0.0001114842, 0.0001114019, 0.0001112826,
        0.0001111318, 0.000110954, 0.000110753,
      ],
      [
        4.253282e-5, 4.435957e-5, 4.658424e-5, 5.025856e-5, 5.657843e-5,
        6.311626e-5, 6.929681e-5, 7.509235e-5, 8.047378e-5, 8.540991e-5,
        8.988046e-5, 9.387789e-5, 9.74014e-5, 0.0001004532, 0.0001030424,
        0.0001051917, 0.0001069398, 0.0001083368, 0.0001094363, 0.000110289,
        0.0001109391, 0.0001114235, 0.0001117724, 0.0001120101, 0.0001121561,
        0.000112226, 0.0001122324, 0.0001121857, 0.0001120943, 0.0001119649,
        0.0001118034, 0.0001116145, 0.000111402,
      ],
      [
        4.288006e-5, 4.477393e-5, 4.698795e-5, 5.047208e-5, 5.659374e-5,
        6.311891e-5, 6.930347e-5, 7.511184e-5, 8.051881e-5, 8.549556e-5,
        9.002117e-5, 9.40854e-5, 9.76839e-5, 0.0001008148, 0.0001034824,
        0.000105704, 0.0001075143, 0.0001089615, 0.0001100992, 0.0001109794,
        0.000111648, 0.0001121438, 0.0001124983, 0.0001127373, 0.0001128811,
        0.0001129461, 0.0001129458, 0.0001128907, 0.0001127897, 0.00011265,
        0.0001124773, 0.0001122767, 0.0001120523,
      ],
      [
        4.324376e-5, 4.519652e-5, 4.740398e-5, 5.072223e-5, 5.661637e-5,
        6.312203e-5, 6.930956e-5, 7.512955e-5, 8.056004e-5, 8.557476e-5,
        9.015279e-5, 9.428173e-5, 9.795411e-5, 0.0001011643, 0.0001039115,
        0.0001062078, 0.0001080832, 0.0001095834, 0.0001107617, 0.0001116712,
        0.0001123596, 0.0001128675, 0.0001132282, 0.0001134685, 0.0001136101,
        0.0001136702, 0.0001136628, 0.000113599, 0.000113488, 0.0001133374,
        0.0001131532, 0.0001129405, 0.0001127036,
      ],
      [
        4.364981e-5, 4.562739e-5, 4.783213e-5, 5.100769e-5, 5.66496e-5,
        6.312593e-5, 6.931522e-5, 7.514566e-5, 8.059773e-5, 8.564788e-5,
        9.027562e-5, 9.4467e-5, 9.821188e-5, 0.000101501, 0.000104329,
        0.0001067023, 0.0001086457, 0.0001102018, 0.0001114232, 0.0001123639,
        0.0001130735, 0.0001135944, 0.0001139616, 0.0001142036, 0.0001143429,
        0.0001143979, 0.0001143831, 0.0001143102, 0.0001141889, 0.0001140269,
        0.0001138307, 0.0001136055, 0.0001133556,
      ],
      [
        4.40626e-5, 4.606658e-5, 4.827219e-5, 5.13264e-5, 5.669777e-5,
        6.313109e-5, 6.932055e-5, 7.516032e-5, 8.063215e-5, 8.571524e-5,
        9.038997e-5, 9.464138e-5, 9.845711e-5, 0.0001018248, 0.0001047344,
        0.0001071867, 0.0001092009, 0.0001108159, 0.000112083, 0.0001130571,
        0.0001137893, 0.0001143242, 0.0001146985, 0.0001149423, 0.0001150794,
        0.000115129, 0.0001151065, 0.0001150242, 0.0001148921, 0.0001147183,
        0.0001145095, 0.0001142712, 0.0001140079,
      ],
      [
        4.448208e-5, 4.651413e-5, 4.872401e-5, 5.1676e-5, 5.676632e-5,
        6.31382e-5, 6.932571e-5, 7.517367e-5, 8.066355e-5, 8.57772e-5,
        9.04962e-5, 9.480509e-5, 9.868977e-5, 0.0001021351, 0.0001051269,
        0.0001076601, 0.000109748, 0.000111425, 0.0001127406, 0.0001137502,
        0.0001145067, 0.0001150565, 0.0001154386, 0.0001156844, 0.0001158192,
        0.0001158633, 0.0001158328, 0.0001157406, 0.0001155972, 0.0001154112,
        0.0001151894, 0.0001149375, 0.0001146602,
      ],
      [
        4.490822e-5, 4.697002e-5, 4.918742e-5, 5.205414e-5, 5.686167e-5,
        6.314831e-5, 6.933089e-5, 7.518587e-5, 8.069219e-5, 8.583409e-5,
        9.059466e-5, 9.495839e-5, 9.890992e-5, 0.0001024318, 0.000105506,
        0.0001081218, 0.0001102861, 0.0001120281, 0.0001133952, 0.0001144427,
        0.0001152251, 0.0001157911, 0.0001161814, 0.0001164296, 0.0001165621,
        0.0001166005, 0.0001165616, 0.0001164591, 0.000116304, 0.0001161052,
        0.0001158698, 0.0001156038, 0.000115312,
      ],
      [
        4.534089e-5, 4.74342e-5, 4.966228e-5, 5.24586e-5, 5.699051e-5,
        6.316296e-5, 6.933635e-5, 7.519709e-5, 8.071829e-5, 8.588623e-5,
        9.068574e-5, 9.510158e-5, 9.911766e-5, 0.0001027147, 0.0001058711,
        0.0001085709, 0.0001108141, 0.0001126245, 0.0001140461, 0.000115134,
        0.0001159442, 0.0001165274, 0.0001169268, 0.0001171775, 0.0001173078,
        0.0001173402, 0.0001172927, 0.0001171795, 0.0001170121, 0.0001168,
        0.0001165504, 0.0001162697, 0.0001159629,
      ],
      [
        4.577998e-5, 4.790662e-5, 5.014842e-5, 5.28874e-5, 5.715893e-5,
        6.318435e-5, 6.934249e-5, 7.520747e-5, 8.074209e-5, 8.593399e-5,
        9.076982e-5, 9.523502e-5, 9.931316e-5, 0.0001029835, 0.0001062218,
        0.0001090065, 0.0001113312, 0.000113213, 0.0001146924, 0.0001158234,
        0.0001166634, 0.0001172652, 0.0001176744, 0.000117928, 0.000118056,
        0.0001180822, 0.0001180256, 0.0001179013, 0.0001177212, 0.0001174951,
        0.0001172309, 0.0001169349, 0.0001166125,
      ],
      [
        4.622531e-5, 4.838715e-5, 5.064565e-5, 5.333876e-5, 5.737152e-5,
        6.321562e-5, 6.934981e-5, 7.521723e-5, 8.076379e-5, 8.597767e-5,
        9.084727e-5, 9.535908e-5, 9.949667e-5, 0.0001032385, 0.0001065576,
        0.0001094281, 0.0001118364, 0.0001137929, 0.0001153333, 0.0001165103,
        0.0001173821, 0.000118004, 0.0001184237, 0.0001186806, 0.0001188063,
        0.0001188262, 0.0001187602, 0.0001186241, 0.0001184307, 0.0001181901,
        0.0001179106, 0.0001175988, 0.0001172602,
      ],
      [
        4.667666e-5, 4.887564e-5, 5.115373e-5, 5.381115e-5, 5.763071e-5,
        6.326108e-5, 6.935911e-5, 7.52266e-5, 8.078364e-5, 8.601759e-5,
        9.091853e-5, 9.547416e-5, 9.966847e-5, 0.0001034794, 0.0001068784,
        0.0001098349, 0.0001123289, 0.0001143631, 0.0001159679, 0.0001171939,
        0.0001180999, 0.0001187433, 0.0001191744, 0.000119435, 0.0001195585,
        0.0001195717, 0.0001194958, 0.0001193476, 0.0001191403, 0.0001188847,
        0.0001185892, 0.0001182609, 0.0001179055,
      ],
      [
        4.713378e-5, 4.937187e-5, 5.167248e-5, 5.430317e-5, 5.793673e-5,
        6.332637e-5, 6.937158e-5, 7.523591e-5, 8.080186e-5, 8.605406e-5,
        9.098398e-5, 9.558068e-5, 9.982891e-5, 0.0001037066, 0.0001071838,
        0.0001102263, 0.0001128076, 0.0001149226, 0.0001165953, 0.0001178733,
        0.000118816, 0.0001194827, 0.0001199262, 0.0001201907, 0.000120312,
        0.0001203184, 0.0001202323, 0.0001200713, 0.0001198496, 0.0001195782,
        0.0001192662, 0.0001189208, 0.0001185479,
      ],
    ],
    cstar: [
      [
        1313.025, 1336.958, 1364.608, 1439.021, 1531.476, 1606.355, 1667.195,
        1716.489, 1755.224, 1783.823, 1803.162, 1814.617, 1819.622, 1819.483,
        1815.4, 1808.468, 1799.608, 1789.528, 1778.735, 1767.578, 1756.289,
        1745.022, 1733.876, 1722.914, 1712.174, 1701.681, 1691.445, 1681.472,
        1671.761, 1662.307, 1653.105, 1644.146, 1635.423,
      ],
      [
        1317.012, 1341.158, 1369.261, 1439.354, 1531.477, 1606.339, 1667.167,
        1716.522, 1755.517, 1784.622, 1804.623, 1816.771, 1822.422, 1822.834,
        1819.188, 1812.575, 1803.933, 1793.993, 1783.282, 1772.167, 1760.892,
        1749.62, 1738.454, 1727.463, 1716.689, 1706.155, 1695.876, 1685.856,
        1676.097, 1666.593, 1657.34, 1648.33, 1639.555,
      ],
      [
        1321.01, 1345.323, 1373.965, 1439.838, 1531.48, 1606.325, 1667.14,
        1716.547, 1755.774, 1785.348, 1805.983, 1818.816, 1825.118, 1826.097,
        1822.906, 1816.63, 1808.22, 1798.43, 1787.808, 1776.741, 1765.483,
        1754.207, 1743.024, 1732.004, 1721.194, 1710.62, 1700.298, 1690.231,
        1680.423, 1670.869, 1661.565, 1652.502, 1643.674,
      ],
      [
        1325.017, 1349.44, 1378.716, 1440.533, 1531.486, 1606.313, 1667.115,
        1716.566, 1755.998, 1786.004, 1807.245, 1820.751, 1827.707, 1829.267,
        1826.548, 1820.627, 1812.464, 1802.836, 1792.312, 1781.297, 1770.06,
        1758.783, 1747.583, 1736.536, 1725.691, 1715.077, 1704.709, 1694.596,
        1684.738, 1675.133, 1665.777, 1656.661, 1647.779,
      ],
      [
        1329.031, 1353.499, 1383.512, 1441.505, 1531.497, 1606.3, 1667.092,
        1716.578, 1756.193, 1786.596, 1808.413, 1822.577, 1830.187, 1832.339,
        1830.11, 1824.562, 1816.663, 1807.207, 1796.791, 1785.833, 1774.621,
        1763.345, 1752.13, 1741.057, 1730.177, 1719.522, 1709.11, 1698.948,
        1689.04, 1679.384, 1669.975, 1660.805, 1651.868,
      ],
      [
        1333.051, 1357.488, 1388.351, 1442.827, 1531.515, 1606.29, 1667.071,
        1716.587, 1756.363, 1787.129, 1809.489, 1824.293, 1832.556, 1835.309,
        1833.588, 1828.432, 1820.813, 1811.543, 1801.241, 1790.348, 1779.165,
        1767.893, 1756.664, 1745.565, 1734.651, 1723.955, 1713.498, 1703.288,
        1693.329, 1683.62, 1674.157, 1664.932, 1655.939,
      ],
      [
        1337.073, 1361.392, 1393.231, 1444.564, 1531.543, 1606.282, 1667.051,
        1716.592, 1756.511, 1787.606, 1810.478, 1825.902, 1834.811, 1838.174,
        1836.976, 1832.231, 1824.909, 1815.838, 1805.662, 1794.84, 1783.689,
        1772.423, 1761.182, 1750.059, 1739.11, 1728.374, 1717.871, 1707.612,
        1697.602, 1687.839, 1678.321, 1669.04, 1659.992,
      ],
      [
        1341.095, 1365.201, 1398.15, 1446.759, 1531.587, 1606.274, 1667.034,
        1716.595, 1756.637, 1788.033, 1811.383, 1827.405, 1836.953, 1840.93,
        1840.271, 1835.955, 1828.947, 1820.09, 1810.049, 1799.305, 1788.192,
        1776.935, 1765.684, 1754.536, 1743.554, 1732.777, 1722.228, 1711.92,
        1701.858, 1692.041, 1682.467, 1673.129, 1664.022,
      ],
      [
        1345.115, 1368.9, 1403.106, 1449.425, 1531.655, 1606.269, 1667.017,
        1716.596, 1756.747, 1788.414, 1812.21, 1828.804, 1838.981, 1843.575,
        1843.467, 1839.6, 1832.924, 1824.296, 1814.4, 1803.741, 1792.671,
        1781.427, 1770.167, 1758.996, 1747.981, 1737.163, 1726.568, 1716.21,
        1706.095, 1696.223, 1686.592, 1677.197, 1668.03,
      ],
      [
        1349.131, 1373.033, 1408.095, 1452.539, 1531.758, 1606.265, 1667.001,
        1716.595, 1756.842, 1788.753, 1812.963, 1830.104, 1840.894, 1846.106,
        1846.561, 1843.159, 1836.835, 1828.45, 1818.712, 1808.147, 1797.124,
        1785.896, 1774.63, 1763.437, 1752.389, 1741.53, 1730.889, 1720.48,
        1710.311, 1700.384, 1690.695, 1681.24, 1672.014,
      ],
      [
        1353.138, 1377.514, 1413.115, 1456.049, 1531.914, 1606.264, 1666.989,
        1716.592, 1756.925, 1789.054, 1813.647, 1831.306, 1842.694, 1848.521,
        1849.549, 1846.629, 1840.675, 1832.55, 1822.983, 1812.519, 1801.55,
        1790.341, 1779.071, 1767.857, 1756.776, 1745.877, 1735.189, 1724.729,
        1714.506, 1704.521, 1694.774, 1685.259, 1675.971,
      ],
      [
        1357.134, 1382.561, 1418.161, 1459.891, 1532.145, 1606.267, 1666.976,
        1716.589, 1756.995, 1789.321, 1814.267, 1832.415, 1844.382, 1850.818,
        1852.426, 1850.006, 1844.439, 1836.592, 1827.207, 1816.855, 1805.945,
        1794.759, 1783.487, 1772.254, 1761.141, 1750.201, 1739.466, 1728.954,
        1718.676, 1708.633, 1698.826, 1689.25, 1679.9,
      ],
      [
        1361.115, 1387.609, 1423.23, 1463.995, 1532.488, 1606.275, 1666.965,
        1716.586, 1757.056, 1789.557, 1814.826, 1833.436, 1845.96, 1852.997,
        1855.19, 1853.283, 1848.123, 1840.572, 1831.383, 1821.152, 1810.308,
        1799.15, 1787.878, 1776.627, 1765.482, 1754.502, 1743.719, 1733.155,
        1722.82, 1712.719, 1702.851, 1693.212, 1683.798,
      ],
      [
        1365.078, 1392.658, 1428.318, 1468.3, 1532.984, 1606.29, 1666.956,
        1716.582, 1757.109, 1789.767, 1815.329, 1834.372, 1847.431, 1855.057,
        1857.837, 1856.457, 1851.723, 1844.484, 1835.507, 1825.407, 1814.636,
        1803.509, 1792.241, 1780.973, 1769.798, 1758.776, 1747.945, 1737.328,
        1726.937, 1716.776, 1706.845, 1697.143, 1687.664,
      ],
      [
        1369.019, 1397.701, 1433.42, 1472.76, 1533.694, 1606.315, 1666.949,
        1716.578, 1757.154, 1789.953, 1815.783, 1835.227, 1848.798, 1856.999,
        1860.366, 1859.524, 1855.234, 1848.325, 1839.575, 1829.617, 1818.926,
        1807.836, 1796.574, 1785.29, 1774.085, 1763.023, 1752.144, 1741.473,
        1731.023, 1720.802, 1710.808, 1701.041, 1691.495,
      ],
      [
        1372.929, 1402.734, 1438.53, 1477.335, 1534.682, 1606.356, 1666.943,
        1716.574, 1757.193, 1790.116, 1816.191, 1836.007, 1850.064, 1858.825,
        1862.774, 1862.48, 1858.65, 1852.092, 1843.583, 1833.779, 1823.177,
        1812.129, 1800.875, 1789.578, 1778.343, 1767.24, 1756.313, 1745.587,
        1735.079, 1724.795, 1714.737, 1704.904, 1695.29,
      ],
      [
        1376.658, 1407.751, 1443.642, 1482.0, 1536.015, 1606.419, 1666.94,
        1716.571, 1757.226, 1790.261, 1816.556, 1836.718, 1851.232, 1860.535,
        1865.06, 1865.32, 1861.968, 1855.778, 1847.528, 1837.891, 1827.385,
        1816.384, 1805.143, 1793.834, 1782.57, 1771.426, 1760.449, 1749.669,
        1739.1, 1728.753, 1718.63, 1708.729, 1699.046,
      ],
      [
        1380.14, 1412.748, 1448.752, 1486.736, 1537.748, 1606.518, 1666.939,
        1716.568, 1757.256, 1790.389, 1816.884, 1837.364, 1852.309, 1862.131,
        1867.224, 1868.043, 1865.182, 1859.379, 1851.405, 1841.948, 1831.549,
        1820.6, 1809.375, 1798.055, 1786.763, 1775.579, 1764.552, 1753.715,
        1743.087, 1732.675, 1722.485, 1712.515, 1702.762,
      ],
      [
        1383.369, 1417.719, 1453.854, 1491.524, 1539.912, 1606.665, 1666.943,
        1716.564, 1757.281, 1790.503, 1817.177, 1837.949, 1853.299, 1863.617,
        1869.266, 1870.644, 1868.288, 1862.891, 1855.211, 1845.948, 1835.664,
        1824.775, 1813.568, 1802.241, 1790.922, 1779.696, 1768.62, 1757.726,
        1747.035, 1736.558, 1726.299, 1716.259, 1706.434,
      ],
      [
        1386.343, 1422.657, 1458.94, 1496.351, 1542.506, 1606.887, 1666.953,
        1716.562, 1757.303, 1790.603, 1817.44, 1838.479, 1854.205, 1864.998,
        1871.186, 1873.123, 1871.283, 1866.309, 1858.941, 1849.887, 1839.729,
        1828.905, 1817.722, 1806.388, 1795.043, 1783.777, 1772.65, 1761.698,
        1750.944, 1740.4, 1730.071, 1719.959, 1710.06,
      ],
      [
        1389.521, 1427.557, 1464.003, 1501.206, 1545.496, 1607.215, 1666.972,
        1716.56, 1757.321, 1790.691, 1817.674, 1838.958, 1855.035, 1866.275,
        1872.986, 1875.477, 1874.162, 1869.629, 1862.592, 1853.761, 1843.741,
        1832.99, 1821.834, 1810.496, 1799.125, 1787.818, 1776.64, 1765.629,
        1754.811, 1744.199, 1733.799, 1723.613, 1713.639,
      ],
      [
        1394.05, 1432.412, 1469.034, 1506.076, 1548.831, 1607.69, 1667.005,
        1716.56, 1757.338, 1790.77, 1817.884, 1839.39, 1855.791, 1867.455,
        1874.67, 1877.706, 1876.922, 1872.846, 1866.158, 1857.567, 1847.696,
        1837.025, 1825.901, 1814.562, 1803.166, 1791.819, 1780.588, 1769.517,
        1758.634, 1747.952, 1737.479, 1727.218, 1717.168,
      ],
      [
        1398.517, 1437.214, 1474.029, 1510.95, 1552.445, 1608.364, 1667.056,
        1716.562, 1757.352, 1790.838, 1818.07, 1839.779, 1856.48, 1868.542,
        1876.238, 1879.81, 1879.559, 1875.955, 1869.635, 1861.302, 1851.591,
        1841.009, 1829.922, 1818.584, 1807.164, 1795.776, 1784.493, 1773.361,
        1762.41, 1751.657, 1741.111, 1730.773, 1720.644,
      ],
      [
        1402.915, 1441.953, 1478.977, 1515.815, 1556.277, 1609.293, 1667.137,
        1716.568, 1757.365, 1790.898, 1818.234, 1840.128, 1857.105, 1869.539,
        1877.696, 1881.789, 1882.073, 1878.954, 1873.02, 1864.96, 1855.425,
        1844.94, 1833.895, 1822.56, 1811.117, 1799.688, 1788.351, 1777.158,
        1766.139, 1755.313, 1744.69, 1734.274, 1724.065,
      ],
      [
        1407.237, 1446.626, 1483.869, 1520.661, 1560.275, 1610.533, 1667.261,
        1716.578, 1757.377, 1790.951, 1818.382, 1840.441, 1857.672, 1870.453,
        1879.046, 1883.645, 1884.461, 1881.836, 1876.307, 1868.54, 1859.192,
        1848.814, 1837.816, 1826.487, 1815.023, 1803.553, 1792.162, 1780.906,
        1769.817, 1758.917, 1748.216, 1737.72, 1727.429,
      ],
    ],
  },
};
